import { refineEditorShell } from './ui-refinements';
import type { Component } from 'grapesjs';
import { componentRegistry, getComponentDefinition } from '../components-registry/registry';
import type { DeviceId, WebKilnProject, PageDocument, ProjectAsset } from '../types';
import {
  calculateAssetUsage,
  computeAssetContentHash,
  extractFolderHierarchy,
  normalizeFolderPath,
} from '../assets/asset-storage';
import { askText, askConfirm, bindDialog } from '../cloud/dialog';
import type { LocalProjectStorage } from '../storage/project-storage';
import { WebKilnEditorAdapter } from './webkiln-editor-adapter';
import {
  duplicatePage,
  newPage,
  removePage,
  restorePage,
  setPageFolder,
  setPageParent,
  setHomepage,
  uniqueSlug,
} from '../models/page-manager';
import { deterministicSuggestion } from './ai-assist';
import {
  addMenuItem,
  ensureMenus,
  findMenuItem,
  moveMenuItem,
  removeMenuItem,
} from '../models/menu-manager';
import { getLayoutPresetStyles, LAYOUT_CONTROLS, type LayoutPreset } from './layout-presets';
import type { FormDefinition, PagePermission, ReviewComment } from '../cloud/contracts';
import type { WebKilnApiClient } from '../cloud/api-client';
import { canonicalRole } from '../cloud/roles';
import { installInteractionRuntime, switchCarouselSlide } from './interaction-runtime';

const blockMap: Record<string, string> = {
  hero: 'hero',
  features: 'columns',
  pricing: 'pricing',
  games: 'gallery',
  quote: 'contact',
  media: 'gallery',
  form: 'contact',
  footer: 'section',
};

const deviceWidths: Record<DeviceId, number> = {
  desktop: 1200,
  laptop: 1024,
  tablet: 768,
  mobile: 390,
};

function firstComponent(value: unknown): Component | null {
  if (!value) return null;
  return Array.isArray(value)
    ? ((value[0] as Component | undefined) ?? null)
    : (value as Component);
}

function componentLabel(component: Component | null): string {
  if (!component) return 'Page';
  return String(component.get('name') || component.get('tagName') || 'Element');
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => resolve('');
    reader.readAsDataURL(file);
  });
}

function escapePageText(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character] ??
      character,
  );
}

interface CarouselSlideData {
  src: string;
  alt: string;
  title: string;
  description: string;
}

export class WebKilnEditorController {
  private activeCarouselSlideIndex = 0;
  private selected: Component | null = null;
  private activeAssetFolder = '';
  private saveTimer: number | undefined;
  private device: DeviceId = 'desktop';
  private previewing = false;
  private runtimeCleanup: (() => void) | null = null;

  syncInteractionRuntime(): void {
    this.runtimeCleanup?.();
    this.runtimeCleanup = null;
    const doc = this.adapter.getFrameDocument();
    if (doc) {
      this.runtimeCleanup = installInteractionRuntime(
        doc,
        this.project.editorSettings?.interactions ?? [],
      );
    }
  }
  private expandedLayerIds = new Set<string>();
  private clipboard: Component | null = null;
  private draggedLayerId: string | null = null;
  private draggedPageId: string | null = null;
  private readonly commands = [
    ['add-section', 'Add section', 'Insert a new hero section'],
    ['add-component', 'Add component', 'Open reusable components'],
    ['switch-page', 'Switch page', 'Open the page manager'],
    ['switch-breakpoint', 'Switch breakpoint', 'Open responsive controls'],
    ['open-layers', 'Open layers', 'Inspect and reorder page layers'],
    ['open-design', 'Open design system', 'Open site theme controls'],
    ['open-health', 'Open site health', 'Open site settings and checks'],
    ['preview', 'Preview', 'Preview the current page'],
    ['publish', 'Publish', 'Save a publish checkpoint'],
    ['save-revision', 'Save revision', 'Create a named local checkpoint'],
    ['search-settings', 'Search settings', 'Open site settings'],
    ['group-selection', 'Group selection', 'Wrap selected elements in a reusable container'],
    [
      'ungroup-selection',
      'Ungroup selection',
      'Remove a selection container without losing children',
    ],
    ['ai-outline', 'Suggest site outline', 'Use WebKiln’s local fallback suggestions'],
    ['ai-seo', 'Suggest SEO description', 'Draft metadata without sending project data externally'],
    ['ai-accessibility', 'Suggest accessibility improvements', 'Review safe, deterministic checks'],
  ] as const;

  private contentOnlyMode = false;
  private role = 'owner';
  private currentUserId = '';
  private workspaceId = '';
  private pagePermissions: PagePermission[] = [];
  private cloudClient?: WebKilnApiClient;
  private reviewComments: ReviewComment[] = [];
  private activeCommentFilter: 'all' | 'open' | 'resolved' = 'all';
  private selectedComponentForComment?: { id: string; name: string } | null = null;
  private workspaceMembers: Array<{ userId: string; role: string; user?: { name?: string; email?: string } }> = [];
  private activeAutocompleteIndex = 0;
  private activeAutocompleteItems: Array<{ type: 'member' | 'page'; id: string; label: string; sub?: string }> = [];
  private activeAutocompleteTrigger: { trigger: '@' | '#'; startIndex: number; query: string } | null = null;

  constructor(
    private readonly adapter: WebKilnEditorAdapter,
    private readonly project: WebKilnProject,
    private readonly storage: LocalProjectStorage,
    private readonly forms: FormDefinition[] = [],
    options?: {
      role?: string;
      contentOnly?: boolean;
      currentUserId?: string;
      pagePermissions?: PagePermission[];
      reviewComments?: ReviewComment[];
      workspaceId?: string;
      cloud?: WebKilnApiClient;
    },
  ) {
    this.role = options?.role ?? 'owner';
    this.contentOnlyMode = Boolean(options?.contentOnly || this.role === 'content_editor');
    this.currentUserId = options?.currentUserId ?? '';
    this.workspaceId = options?.workspaceId ?? '';
    this.pagePermissions = options?.pagePermissions ?? [];
    this.reviewComments = options?.reviewComments ?? [];
    this.cloudClient = options?.cloud;
  }

  canEditPage(pageId: string): boolean {
    const role = canonicalRole(this.role);
    if (role === 'owner' || role === 'administrator') return true;
    if (role === 'viewer' || role === 'reviewer') return false;

    if (!this.currentUserId || !this.pagePermissions.length) return true;
    const userPermissions = this.pagePermissions.filter(
      (p) => p.userId === this.currentUserId,
    );
    if (!userPermissions.length) return true;

    const pagePerm = userPermissions.find((p) => p.pageId === pageId);
    if (!pagePerm) return false;
    return pagePerm.role !== 'viewer';
  }

  setContentEditorMode(enabled: boolean): void {
    this.contentOnlyMode = enabled;
    this.applyContentEditorModeUI();
    if (typeof document !== 'undefined') {
      this.renderDynamicInspector(this.contentOnlyMode ? 'content' : 'content');
    }
  }

  isContentEditorMode(): boolean {
    return this.contentOnlyMode;
  }

  private applyContentEditorModeUI(): void {
    if (typeof document === 'undefined') return;
    const shell = document.querySelector('.editor-shell');
    const badge = document.querySelector('#contentModeBadge');
    const toggleBtn = document.querySelector<HTMLButtonElement>('#toggleClientViewBtn');

    if (this.contentOnlyMode) {
      shell?.classList.add('client-content-mode');
      if (!badge) {
        const span = document.createElement('span');
        span.id = 'contentModeBadge';
        span.className = 'content-mode-badge';
        span.innerHTML =
          '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg><span>Client Content Mode</span>';
        const topActions = document.querySelector('.top-actions');
        topActions?.prepend(span);
      }
      if (toggleBtn) {
        toggleBtn.classList.add('active');
        toggleBtn.innerHTML =
          '<svg class="btn-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg><span>Exit client mode</span>';
      }
    } else {
      shell?.classList.remove('client-content-mode');
      badge?.remove();
      if (toggleBtn) {
        toggleBtn.classList.remove('active');
        toggleBtn.innerHTML =
          '<svg class="btn-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg><span>Preview client mode</span>';
      }
    }

    if (this.role === 'content_editor' && toggleBtn) {
      toggleBtn.style.display = 'none';
    }
  }

  private applyPageReadOnlyState(isReadOnly: boolean): void {
    if (typeof document === 'undefined') return;
    const shell = document.querySelector('.editor-shell');
    const existingBanner = document.querySelector('#pageReadOnlyBanner');

    if (isReadOnly) {
      shell?.classList.add('page-read-only');
      if (!existingBanner) {
        const banner = document.createElement('div');
        banner.id = 'pageReadOnlyBanner';
        banner.className = 'page-readonly-banner';
        banner.innerHTML =
          '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg><span>Read-only page · Edits restricted</span>';
        document.querySelector('.top-actions')?.prepend(banner);
      }
    } else {
      shell?.classList.remove('page-read-only');
      existingBanner?.remove();
    }
  }

  start(): void {
    this.setDevice(window.matchMedia('(max-width: 760px)').matches ? 'mobile' : 'desktop');
    const currentPage = this.project.pages.find((page) => page.id === this.project.currentPageId);
    const crumb = document.querySelector('.crumb span');
    if (crumb && currentPage) crumb.textContent = currentPage.name;
    this.bindShell();
    refineEditorShell();
    this.applyContentEditorModeUI();
    const canEdit = this.canEditPage(this.project.currentPageId);
    this.applyPageReadOnlyState(!canEdit);
    const toggleBtn = document.querySelector<HTMLButtonElement>('#toggleClientViewBtn');
    toggleBtn?.addEventListener('click', () => {
      this.setContentEditorMode(!this.contentOnlyMode);
    });
    this.bindBlocks();
    this.bindAssets();
    this.bindPages();
    this.bindInspector();
    this.bindLayers();
    this.bindReviewComments();
    this.adapter.subscribe('select', (component) =>
      this.onSelect((component as Component) ?? null),
    );
    this.adapter.subscribe('update', () => {
      this.syncInteractionRuntime();
      this.scheduleSave();
    });
    this.adapter.subscribe('load', () => this.syncInteractionRuntime());
    this.syncInteractionRuntime();
    this.adapter.subscribe('device', (device) => {
      const nextDevice = device as DeviceId;
      if (['desktop', 'laptop', 'tablet', 'mobile'].includes(nextDevice)) this.device = nextDevice;
      this.syncDeviceButtons(nextDevice);
      this.setCanvasStatus(nextDevice);
      requestAnimationFrame(() => this.fitCanvas());
    });
    this.onSelect(this.adapter.getSelectedComponent());
    this.renderLayers();
    this.renderPages();
    this.scheduleSave();
  }

  private bindResponsivePanels(): void {
    const shell = document.querySelector<HTMLElement>('.editor-shell');
    const actions = document.querySelector('.top-actions');
    if (!shell || !actions) return;
    for (const [label, className, panelSelector] of [
      ['Tools', 'tools-open', '.left-rail'],
      ['Inspector', 'inspector-open', '.inspector'],
    ]) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `ghost-btn responsive-panel-toggle ${className}-toggle`;
      button.textContent = label;
      button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', async () => {
        const opened = !shell.classList.contains(className);
        shell.classList.remove('tools-open', 'inspector-open');
        shell.classList.toggle(className, opened);
        document
          .querySelectorAll('.responsive-panel-toggle')
          .forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
        button.setAttribute('aria-expanded', String(opened));
      });
      actions.prepend(button);
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'ghost-btn responsive-panel-close';
      close.textContent = `Close ${label.toLowerCase()}`;
      close.addEventListener('click', async () => {
        shell.classList.remove(className);
        button.setAttribute('aria-expanded', 'false');
        button.focus();
      });
      shell.querySelector(panelSelector)?.prepend(close);
    }
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && shell.matches('.tools-open, .inspector-open')) {
        shell.querySelector<HTMLButtonElement>(
          '.tools-open .responsive-panel-close, .inspector-open .responsive-panel-close',
        );
        shell.classList.remove('tools-open', 'inspector-open');
        document
          .querySelectorAll('.responsive-panel-toggle')
          .forEach((toggle) => toggle.setAttribute('aria-expanded', 'false'));
      }
    });
  }

  private bindShell(): void {
    this.bindResponsivePanels();
    document.querySelector('#undoBtn')?.addEventListener('click', () => this.adapter.undo());
    document.querySelector('#redoBtn')?.addEventListener('click', () => this.adapter.redo());
    document
      .querySelectorAll<HTMLButtonElement>('.device')
      .forEach((button) =>
        button.addEventListener('click', () => this.setDevice(button.dataset.width as DeviceId)),
      );
    const fit = document.createElement('button');
    fit.type = 'button';
    fit.textContent = 'Fit';
    fit.title = 'Fit canvas to available width';
    fit.id = 'fitCanvas';
    document.querySelector('#zoomValue')?.parentElement?.append(fit);
    fit.addEventListener('click', () => this.fitCanvas());
    const canvasArea = document.querySelector('#canvasWrap');
    if (canvasArea) new ResizeObserver(() => this.fitCanvas()).observe(canvasArea);
    document.querySelector('#zoomOut')?.addEventListener('click', () => this.changeZoom(-10));
    document.querySelector('#zoomIn')?.addEventListener('click', () => this.changeZoom(10));
    document.querySelector('#setupBtn')?.addEventListener('click', async () => {
      document
        .querySelector('#openSiteSetup')
        ?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
    });
    document.querySelector('#helpBtn')?.addEventListener('click', async () => {
      this.toast(
        'WebKiln help',
        'Select a section to edit it, or press Ctrl / Cmd + K to open commands.',
      );
    });
    document.querySelector('#healthBtn')?.addEventListener('click', async () => {
      document.querySelector<HTMLButtonElement>('.rail-tab[data-panel="site"]')?.click();
      document.querySelector<HTMLButtonElement>('[data-run-health]')?.click();
    });
    document.querySelector('#deleteBtn')?.addEventListener('click', () => this.deleteSelected());
    document
      .querySelector('.outline-btn')
      ?.addEventListener('click', () => this.duplicateSelected());
    document.querySelector('#previewBtn')?.addEventListener('click', () => this.togglePreview());
    document.querySelector('#shareBtn')?.addEventListener('click', async () => {
      const url = window.location.href;
      this.toast('Share link ready', url);
      const copy = navigator.clipboard?.writeText(url);
      if (copy) {
        void copy
          .then(() =>
            this.toast(
              'Share link copied',
              'The current WebKiln editor link is on your clipboard.',
            ),
          )
          .catch(() => undefined);
      }
    });
    document
      .querySelector('#publishBtn')
      ?.addEventListener('click', () => this.saveNow('Published checkpoint'));
    document.addEventListener('keydown', (event) => this.handleShortcut(event));
    this.bindCommandPalette();
    document.querySelectorAll<HTMLButtonElement>('.rail-tab').forEach((button) =>
      button.addEventListener('click', async () => {
        document.querySelectorAll('.rail-tab').forEach((item) => item.classList.remove('active'));
        document.querySelectorAll('.panel').forEach((item) => item.classList.remove('active'));
        button.classList.add('active');
        document.querySelector(`#${button.dataset.panel}Panel`)?.classList.add('active');
      }),
    );
    document
      .querySelector('#aiStarterBtn')
      ?.addEventListener('click', () => this.addBlock('features'));
    document.querySelector('#themeColor')?.addEventListener('input', (event) => {
      const value = (event.target as HTMLInputElement).value;
      document.documentElement.style.setProperty('--gold', value);
      this.project.themeTokens.primary = value;
      this.scheduleSave();
    });
    document.querySelector('#radiusSelect')?.addEventListener('change', (event) => {
      const value = (event.target as HTMLSelectElement).value;
      document.documentElement.style.setProperty('--radius', `${value}px`);
      this.project.themeTokens.radius = `${value}px`;
      this.scheduleSave();
    });
    document
      .querySelector('#checkpointBtn')
      ?.addEventListener('click', () => this.saveNow('Named checkpoint'));
    this.bindCustomCode();
    this.bindSiteSetup();
    this.bindRecovery();
  }

  private bindCommandPalette(): void {
    document
      .querySelector('#commandBtn')
      ?.addEventListener('click', () => this.openCommandPalette());
    document
      .querySelector('#closeCommand')
      ?.addEventListener('click', () => this.closeCommandPalette());
    document.querySelector('#commandPalette')?.addEventListener('click', async (event) => {
      if (event.target === event.currentTarget) this.closeCommandPalette();
    });
    document.querySelector('#commandSearch')?.addEventListener('input', (event) => {
      this.renderCommandList((event.target as HTMLInputElement).value);
    });
    document.querySelector('#commandList')?.addEventListener('click', async (event) => {
      const command = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-command]');
      if (command) this.executeCommand(command.dataset.command ?? '');
    });
  }

  private openCommandPalette(): void {
    const palette = document.querySelector<HTMLElement>('#commandPalette');
    if (!palette) return;
    palette.hidden = false;
    this.renderCommandList();
    document.querySelector<HTMLInputElement>('#commandSearch')?.focus();
  }

  private closeCommandPalette(): void {
    const palette = document.querySelector<HTMLElement>('#commandPalette');
    if (palette) palette.hidden = true;
  }

  private renderCommandList(query = ''): void {
    const list = document.querySelector<HTMLElement>('#commandList');
    if (!list) return;
    const normalized = query.trim().toLowerCase();
    const commands = this.commands.filter(([id, label, description]) =>
      `${id} ${label} ${description}`.toLowerCase().includes(normalized),
    );
    list.innerHTML = commands.length
      ? commands
        .map(
          ([id, label, description]) =>
            `<button type="button" data-command="${id}" role="option"><strong>${label}</strong><small>${description}</small></button>`,
        )
        .join('')
      : '<p class="command-empty">No matching commands</p>';
  }

  private executeCommand(command: string): void {
    this.closeCommandPalette();
    if (command === 'add-section') this.addBlock('hero');
    if (command === 'add-component') this.openPanel('components');
    if (command === 'switch-page') this.openPanel('pages');
    if (command === 'switch-breakpoint')
      document
        .querySelector<HTMLElement>('.editing-experience-toolbar')
        ?.scrollIntoView({ behavior: 'smooth' });
    if (command === 'open-layers') this.openPanel('layers');
    if (command === 'open-design') this.openPanel('theme');
    if (command === 'open-health' || command === 'search-settings') this.openPanel('site');
    if (command === 'preview') this.togglePreview();
    if (command === 'publish') this.saveNow('Published checkpoint');
    if (command === 'save-revision') this.saveNow('Named checkpoint');
    if (command === 'ai-outline') this.showAiSuggestion('site-outline');
    if (command === 'ai-seo') this.showAiSuggestion('seo-description');
    if (command === 'ai-accessibility') this.showAiSuggestion('accessibility');
    if (command === 'group-selection') this.groupSelected();
    if (command === 'ungroup-selection') this.ungroupSelected();
  }

  private showAiSuggestion(kind: 'site-outline' | 'seo-description' | 'accessibility'): void {
    this.toast(
      'Local suggestion · AI provider not connected',
      deterministicSuggestion(kind, this.project),
    );
  }

  private openPanel(panel: string): void {
    if (window.matchMedia('(max-width: 760px)').matches)
      document.querySelector('.editor-shell')?.classList.add('tools-open');
    document
      .querySelectorAll('.rail-tab')
      .forEach((item) =>
        item.classList.toggle('active', (item as HTMLElement).dataset.panel === panel),
      );
    document
      .querySelectorAll('.panel')
      .forEach((item) => item.classList.toggle('active', item.id === `${panel}Panel`));
    if (panel === 'comments') {
      this.renderReviewComments();
    }
  }

  private bindBlocks(): void {
    this.populateComponentCatalog();
    const search = document.querySelector<HTMLInputElement>('#blockSearch');
    const categories = document.querySelector('#blocksPanel .category-row');
    if (categories)
      categories.innerHTML = ['all', 'layout', 'commerce', 'media', 'forms', 'recent']
        .map(
          (filter) =>
            `<button type="button" class="chip ${filter === 'all' ? 'active' : ''}" data-filter="${filter}">${filter === 'recent' ? 'Recently used' : filter.charAt(0).toUpperCase() + filter.slice(1)}</button>`,
        )
        .join('');
    let activeFilter = 'all';
    const applyFilter = () => {
      const query = search?.value.toLowerCase().trim() ?? '';
      let recent: string[] = [];
      try {
        recent = JSON.parse(localStorage.getItem('webkiln-recent-blocks') ?? '[]');
      } catch {
        /* Optional preference. */
      }
      let count = 0;
      document.querySelectorAll<HTMLElement>('.block-card').forEach((card) => {
        const category =
          card.dataset.type === 'media'
            ? 'media'
            : card.dataset.type === 'form'
              ? 'forms'
              : card.dataset.category;
        card.hidden =
          !card.innerText.toLowerCase().includes(query) ||
          (activeFilter === 'recent'
            ? !recent.includes(card.dataset.type ?? '')
            : activeFilter !== 'all' && category !== activeFilter);
        if (!card.hidden) count++;
      });
      const empty = document.querySelector<HTMLElement>('[data-block-empty]');
      if (empty) {
        empty.hidden = count > 0;
        empty.querySelector('p')!.textContent =
          activeFilter === 'recent' && !query
            ? 'Add a section to start your recently used list.'
            : 'Try another category or clear your search.';
      }
    };
    const empty = document.createElement('div');
    empty.dataset.blockEmpty = 'true';
    empty.className = 'editor-empty';
    empty.hidden = true;
    empty.innerHTML =
      '<strong>No sections here yet</strong><p></p><button type="button" class="ghost-btn">Show all sections</button>';
    document.querySelector('#blockList')?.after(empty);
    empty.querySelector('button')!.onclick = () => {
      if (search) search.value = '';
      categories?.querySelector<HTMLButtonElement>('[data-filter="all"]')?.click();
    };
    search?.addEventListener('input', applyFilter);
    document.querySelectorAll<HTMLButtonElement>('#blocksPanel .chip').forEach(
      (chip) =>
      (chip.onclick = () => {
        categories
          ?.querySelectorAll('.chip')
          .forEach((item) => item.classList.toggle('active', item === chip));
        activeFilter = chip.dataset.filter ?? 'all';
        applyFilter();
      }),
    );
    const componentSearch = document.querySelector<HTMLInputElement>('#componentSearch');
    componentSearch?.addEventListener('input', () => {
      const query = componentSearch.value.toLowerCase().trim();
      document.querySelectorAll<HTMLElement>('.component-row').forEach((row) => {
        const text = (row.textContent ?? '').toLowerCase();
        const id = (row.dataset.component ?? '').toLowerCase();
        row.hidden = query.length > 0 && !text.includes(query) && !id.includes(query);
      });
    });
    document.querySelectorAll<HTMLElement>('.block-card').forEach((card) => {
      card.title = `Add ${card.querySelector('strong')?.textContent ?? 'section'}`;
      card.addEventListener('click', async () => {
        this.addBlock(card.dataset.type ?? 'hero');
        applyFilter();
      });
      card.addEventListener('dragstart', (event) => {
        event.dataTransfer?.setData('text/plain', card.dataset.type ?? 'hero');
      });
    });
    const canvasWrap = document.querySelector('#canvasWrap');
    canvasWrap?.addEventListener('dragover', (event) => {
      event.preventDefault();
      canvasWrap.classList.add('drop-ready');
    });
    canvasWrap?.addEventListener('dragleave', () => canvasWrap.classList.remove('drop-ready'));
    canvasWrap?.addEventListener('drop', (event) => {
      const dropEvent = event as DragEvent;
      event.preventDefault();
      canvasWrap.classList.remove('drop-ready');
      this.addBlock(dropEvent.dataTransfer?.getData('text/plain') ?? 'hero');
    });
    document
      .querySelectorAll<HTMLButtonElement>('.component-row')
      .forEach((row) =>
        row.addEventListener('click', () => this.addDefinition(row.dataset.component ?? 'heading')),
      );
  }

  private populateComponentCatalog(): void {
    const catalog = document.querySelector<HTMLElement>('.component-groups');
    if (!catalog) return;
    const existing = new Set(
      Array.from(catalog.querySelectorAll<HTMLElement>('[data-component]')).map(
        (item) => item.dataset.component,
      ),
    );
    const additions = componentRegistry.filter((definition) => !existing.has(definition.id));
    if (!additions.length) return;
    const heading = document.createElement('p');
    heading.className = 'subheading registry-components-heading';
    heading.textContent = 'Full catalog';
    catalog.append(heading);
    additions.forEach((definition) => {
      const button = document.createElement('button');
      button.className = 'component-row';
      button.type = 'button';
      button.dataset.component = definition.id;
      button.title = definition.explanation ?? definition.displayName;
      const icon = document.createElement('span');
      icon.textContent = definition.icon;
      const copy = document.createElement('div');
      const name = document.createElement('strong');
      name.textContent = definition.displayName;
      const description = document.createElement('small');
      description.textContent = definition.smartSection?.purpose ?? 'Responsive WebKiln component';
      copy.append(name, description);
      button.append(icon, copy);
      catalog.append(button);
    });
  }

  private updateAllAssetUsage(): void {
    if (!this.project.assets) this.project.assets = [];
    for (const asset of this.project.assets) {
      asset.usageCount = calculateAssetUsage(this.project, asset.id, asset.name);
    }
  }

  private bindAssets(): void {
    const input = document.querySelector<HTMLInputElement>('#assetInput');
    const drop = document.querySelector<HTMLElement>('#assetDrop');
    const openPicker = () => input?.click();
    document.querySelector('#assetUploadButton')?.addEventListener('click', openPicker);
    document.querySelector('#chooseAsset')?.addEventListener('click', openPicker);
    input?.addEventListener('change', () => {
      if (input.files) void this.addLocalAssetMetadata([...input.files]);
      input.value = '';
    });
    drop?.addEventListener('dragover', (event) => {
      event.preventDefault();
      drop.classList.add('drag-active');
    });
    drop?.addEventListener('dragleave', () => drop.classList.remove('drag-active'));
    drop?.addEventListener('drop', (event) => {
      event.preventDefault();
      drop.classList.remove('drag-active');
      const files = (event as DragEvent).dataTransfer?.files;
      if (files) void this.addLocalAssetMetadata([...files]);
    });
    const search = document.querySelector<HTMLInputElement>('#assetSearch');
    search?.addEventListener('input', () => {
      const query = search.value.toLowerCase().trim();
      document.querySelectorAll<HTMLElement>('.asset-card').forEach((card) => {
        card.hidden = !card.innerText.toLowerCase().includes(query);
      });
    });
    this.renderAssetList();
  }

  private async addLocalAssetMetadata(files: File[]): Promise<void> {
    const accepted = files.filter((file) => file.size > 0);
    if (!accepted.length) return;
    for (const file of accepted) {
      let dims: { width?: number; height?: number } = {};
      let url: string | undefined;
      if (file.type.startsWith('image/')) {
        url = await readFileAsDataUrl(file);
        dims = await new Promise((res) => {
          const img = new Image();
          img.onload = () => res({ width: img.naturalWidth, height: img.naturalHeight });
          img.onerror = () => res({});
          img.src = url!;
        });
      }
      const hash = computeAssetContentHash(file.name, file.size, file.type || 'application/octet-stream');
      const asset: ProjectAsset = {
        id: `asset-${crypto.randomUUID().slice(0, 8)}`,
        name: file.name,
        mime: file.type || 'application/octet-stream',
        size: file.size,
        url,
        folder: this.activeAssetFolder || '/',
        tags: [],
        altText: file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '),
        caption: '',
        focalPoint: { x: 50, y: 50 },
        width: dims.width,
        height: dims.height,
        contentHash: hash,
        usageCount: 0,
        storageStatus: 'local',
      };
      this.project.assets.push(asset);
    }
    this.updateAllAssetUsage();
    this.saveNow('Assets updated');
    this.renderAssetList();
    this.toast(
      'Asset added',
      `${accepted.length} file${accepted.length === 1 ? '' : 's'} indexed in project library.`,
    );
  }

  private renderAssetList(): void {
    const list = document.querySelector<HTMLElement>('#assetList');
    if (!list) return;
    this.updateAllAssetUsage();
    const assets = this.project.assets || [];
    const folders = extractFolderHierarchy(assets.map((a) => ({ folder: a.folder || '/' })));

    if (!assets.length) {
      list.innerHTML =
        '<div class="editor-empty"><strong>Your asset library starts here</strong><p>Add files to index their metadata with this project.</p><button type="button" class="primary-btn" data-empty-assets>Add asset</button></div>';
      list
        .querySelector('[data-empty-assets]')
        ?.addEventListener('click', () =>
          document.querySelector<HTMLInputElement>('#assetInput')?.click(),
        );
      return;
    }

    const folderBar =
      folders.length > 1
        ? `<div class="asset-folders-bar" style="margin-bottom:8px;width:100%;">
            <button type="button" class="folder-pill ${this.activeAssetFolder === '' ? 'active' : ''}" data-editor-folder="">All (${assets.length})</button>
            ${folders.map((f) => `<button type="button" class="folder-pill ${this.activeAssetFolder === f ? 'active' : ''}" data-editor-folder="${escapePageText(f)}">${escapePageText(f)}</button>`).join('')}
          </div>`
        : '';

    const filtered = this.activeAssetFolder
      ? assets.filter((a) => (a.folder || '/') === this.activeAssetFolder)
      : assets;

    list.innerHTML =
      folderBar +
      filtered
        .map((asset) => `
        <article class="asset-card" data-asset-id="${asset.id}">
          <div class="asset-thumb">
            ${asset.mime.startsWith('image/') && asset.url
            ? `<img src="${asset.url}" alt="${escapePageText(asset.altText || asset.name)}" style="width:100%;height:100%;object-fit:cover;object-position:${asset.focalPoint?.x ?? 50}% ${asset.focalPoint?.y ?? 50}%;" />`
            : `<span class="asset-type">${escapePageText(asset.mime.split('/')[1] ?? 'file').toUpperCase()}</span>`
          }
          </div>
          <div style="padding:6px 8px;">
            <strong title="${escapePageText(asset.name)}">${escapePageText(asset.name)}</strong>
            <small>${Math.max(1, Math.round(asset.size / 1024))} KB · <span class="badge-dot ${asset.usageCount ? 'dot-active' : ''}"></span> ${asset.usageCount ? `${asset.usageCount} in use` : 'Unused'}</small>
          </div>
          <div class="asset-actions">
            <button type="button" data-insert-asset="${asset.id}" title="Insert into page">Insert</button>
            <button type="button" data-edit-asset="${asset.id}" title="Edit metadata & focal point">Edit</button>
            <button type="button" data-delete-asset="${asset.id}" ${asset.usageCount ? 'disabled title="Cannot delete: currently in use"' : ''}>Del</button>
          </div>
        </article>
      `)
        .join('');

    list.querySelectorAll<HTMLButtonElement>('[data-editor-folder]').forEach((pill) => {
      pill.addEventListener('click', () => {
        this.activeAssetFolder = pill.dataset.editorFolder ?? '';
        this.renderAssetList();
      });
    });

    list.querySelectorAll<HTMLButtonElement>('[data-insert-asset]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.insertAsset;
        const asset = assets.find((a) => a.id === id);
        if (asset) this.insertAssetIntoPage(asset);
      });
    });

    list.querySelectorAll<HTMLButtonElement>('[data-edit-asset]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.editAsset;
        const asset = assets.find((a) => a.id === id);
        if (asset) this.openAssetEditModal(asset);
      });
    });

    list.querySelectorAll<HTMLButtonElement>('[data-delete-asset]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.deleteAsset;
        const asset = assets.find((a) => a.id === id);
        if (!asset || asset.usageCount) return;
        if (!(await askConfirm(`Delete "${asset.name}" from project library?`))) return;
        const idx = this.project.assets.findIndex((a) => a.id === id);
        if (idx !== -1) {
          this.project.assets.splice(idx, 1);
          this.saveNow('Asset deleted');
          this.renderAssetList();
          this.toast('Asset removed', `Deleted "${asset.name}".`);
        }
      });
    });
  }

  private insertAssetIntoPage(asset: ProjectAsset): void {
    const src = asset.url || `/api/assets/${asset.id}`;
    const focal = asset.focalPoint ?? { x: 50, y: 50 };
    const newComp = this.adapter.addComponent({
      tagName: 'img',
      attributes: {
        src,
        alt: asset.altText || asset.name,
        'data-asset-id': asset.id,
        class: 'wk-image',
      },
      style: {
        'max-width': '100%',
        height: 'auto',
        'object-position': `${focal.x}% ${focal.y}%`,
      },
    });
    if (newComp) {
      this.adapter.selectComponent(newComp);
    }
    this.updateAllAssetUsage();
    this.saveNow('Inserted asset into page');
    this.renderAssetList();
    this.toast('Image inserted', `Inserted "${asset.name}" onto canvas.`);
  }

  private applyAssetToComponent(component: Component, asset: ProjectAsset): void {
    const src = asset.url || `/api/assets/${asset.id}`;
    const focal = asset.focalPoint ?? { x: 50, y: 50 };
    const compStyles = (component.getStyle?.() ?? {}) as Record<string, string>;
    const fit = compStyles['object-fit'] || 'cover';
    const height = compStyles.height && compStyles.height !== 'auto' ? compStyles.height : '280px';
    component.addAttributes({
      src,
      alt: asset.altText || asset.name,
      'data-asset-id': asset.id,
    });
    this.adapter.updateComponentTraits(component, {
      src,
      alt: asset.altText || asset.name,
      'data-asset-id': asset.id,
    });
    component.addStyle({
      width: compStyles.width || '100%',
      height,
      'object-fit': fit,
      'object-position': `${focal.x}% ${focal.y}%`,
    });
    const frameDoc = this.adapter.getFrameDocument();
    const cid = component.getId?.();
    const el = (
      (component.view?.el as HTMLElement | undefined) ??
      ((component.getView?.() as { el?: HTMLElement })?.el as HTMLElement | undefined) ??
      (cid && frameDoc ? (frameDoc.getElementById(cid) as HTMLElement | null) : null) ??
      (frameDoc ? (frameDoc.querySelector(`img[src="${src}"]`) as HTMLElement | null) : null)
    );
    if (el) {
      el.setAttribute('src', src);
      el.setAttribute('alt', asset.altText || asset.name);
      el.style.width = compStyles.width || '100%';
      el.style.height = height;
      el.style.objectFit = fit;
      el.style.objectPosition = `${focal.x}% ${focal.y}%`;
    }
    this.updateAllAssetUsage();
    this.saveNow('Applied asset to image');
    this.renderAssetList();
    this.onSelect(component);
    this.toast('Image updated', `Applied "${asset.name}" to image.`);
  }

  private openAssetPickerModal(onSelect: (asset: ProjectAsset) => void): void {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    const assets = this.project.assets || [];
    const folders = extractFolderHierarchy(assets.map((a) => ({ folder: a.folder || '/' })));
    let activeFolder = '';
    let selectedAssetId: string | null = null;

    const renderGridHtml = (filterFolder: string, query: string) => {
      const filtered = assets.filter((a) => {
        if (filterFolder && (a.folder || '/') !== filterFolder) return false;
        if (query && !a.name.toLowerCase().includes(query)) return false;
        return true;
      });
      if (!filtered.length) {
        return '<div style="padding: 24px; text-align: center; color: #8c9699; grid-column: 1/-1;">No assets found in this folder.</div>';
      }
      return filtered
        .map(
          (a) => `
        <article class="asset-picker-item ${selectedAssetId === a.id ? 'selected' : ''}" data-picker-asset="${a.id}">
          <div class="asset-picker-thumb">
            ${a.mime.startsWith('image/') && a.url
              ? `<img src="${a.url}" alt="${escapePageText(a.name)}" />`
              : `<span class="asset-type">${escapePageText(a.mime.split('/')[1] ?? 'file').toUpperCase()}</span>`
            }
          </div>
          <div class="asset-picker-meta">
            <strong title="${escapePageText(a.name)}">${escapePageText(a.name)}</strong>
            <small>${Math.max(1, Math.round(a.size / 1024))} KB ${a.width && a.height ? `· ${a.width}×${a.height}` : ''}</small>
          </div>
        </article>
      `,
        )
        .join('');
    };

    backdrop.innerHTML = `
      <section class="setup-modal asset-picker-modal" role="dialog" aria-modal="true" aria-labelledby="picker-title">
        <div class="modal-head">
          <h2 id="picker-title">Choose from asset library</h2>
          <button type="button" class="mini-btn" data-close aria-label="Close dialog">×</button>
        </div>
        <div style="display:flex;gap:8px;margin:12px 0 8px;flex-wrap:wrap;">
          <input type="search" data-picker-search placeholder="Search filename..." style="flex:1;min-width:180px;height:36px;background:#0d1012;border:1px solid var(--line);border-radius:6px;color:#fff;padding:0 10px;" />
          <div class="asset-folders-bar" style="margin:0;" data-picker-folders>
            <button type="button" class="folder-pill active" data-picker-folder="">All</button>
            ${folders.map((f) => `<button type="button" class="folder-pill" data-picker-folder="${escapePageText(f)}">${escapePageText(f)}</button>`).join('')}
          </div>
        </div>
        <div class="asset-picker-grid" data-picker-grid>
          ${renderGridHtml('', '')}
        </div>
        <div class="modal-actions" style="margin-top:14px;">
          <button type="button" class="ghost-btn" data-close>Cancel</button>
          <button type="button" class="primary-btn" data-confirm-btn disabled>Use this asset</button>
        </div>
      </section>
    `;

    document.body.append(backdrop);
    const close = () => {
      release();
      backdrop.remove();
    };
    const release = bindDialog(backdrop, close);
    backdrop.querySelectorAll('[data-close]').forEach((btn) => btn.addEventListener('click', close));

    const grid = backdrop.querySelector<HTMLElement>('[data-picker-grid]')!;
    const confirmBtn = backdrop.querySelector<HTMLButtonElement>('[data-confirm-btn]')!;
    const searchInput = backdrop.querySelector<HTMLInputElement>('[data-picker-search]')!;

    const bindItems = () => {
      grid.querySelectorAll<HTMLElement>('[data-picker-asset]').forEach((item) => {
        item.addEventListener('click', () => {
          selectedAssetId = item.dataset.pickerAsset ?? null;
          grid.querySelectorAll('.asset-picker-item').forEach((i) => i.classList.remove('selected'));
          item.classList.add('selected');
          confirmBtn.disabled = !selectedAssetId;
        });
        item.addEventListener('dblclick', () => {
          selectedAssetId = item.dataset.pickerAsset ?? null;
          if (selectedAssetId) {
            const chosen = assets.find((a) => a.id === selectedAssetId);
            if (chosen) {
              onSelect(chosen);
              close();
            }
          }
        });
      });
    };

    const updateFilter = () => {
      const q = searchInput.value.toLowerCase().trim();
      grid.innerHTML = renderGridHtml(activeFolder, q);
      bindItems();
      confirmBtn.disabled = !selectedAssetId;
    };

    searchInput.addEventListener('input', updateFilter);

    backdrop.querySelectorAll<HTMLButtonElement>('[data-picker-folder]').forEach((pill) => {
      pill.addEventListener('click', () => {
        backdrop.querySelectorAll('[data-picker-folder]').forEach((p) => p.classList.remove('active'));
        pill.classList.add('active');
        activeFolder = pill.dataset.pickerFolder ?? '';
        updateFilter();
      });
    });

    confirmBtn.addEventListener('click', () => {
      if (!selectedAssetId) return;
      const chosen = assets.find((a) => a.id === selectedAssetId);
      if (chosen) {
        onSelect(chosen);
        close();
      }
    });

    bindItems();
  }

  private openAssetEditModal(asset: ProjectAsset): void {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    const focal = asset.focalPoint ?? { x: 50, y: 50 };
    backdrop.innerHTML = `
      <section class="setup-modal" role="dialog" aria-modal="true" aria-labelledby="edit-title" style="max-width:520px;">
        <div class="modal-head">
          <h2 id="edit-title">Edit asset details</h2>
          <button type="button" class="mini-btn" data-close aria-label="Close dialog">×</button>
        </div>
        <form data-edit-asset-form style="margin-top:12px;display:grid;gap:10px;">
          <label class="modal-field-label">Filename
            <input type="text" data-edit-name value="${escapePageText(asset.name)}" style="width:100%;height:36px;background:#0d1012;border:1px solid var(--line);border-radius:6px;color:#fff;padding:0 10px;margin-top:4px;" />
          </label>
          <label class="modal-field-label">Alt text (SEO & accessibility)
            <input type="text" data-edit-alt value="${escapePageText(asset.altText || '')}" placeholder="Describe the asset" style="width:100%;height:36px;background:#0d1012;border:1px solid var(--line);border-radius:6px;color:#fff;padding:0 10px;margin-top:4px;" />
          </label>
          <label class="modal-field-label">Caption
            <input type="text" data-edit-caption value="${escapePageText(asset.caption || '')}" placeholder="Optional caption" style="width:100%;height:36px;background:#0d1012;border:1px solid var(--line);border-radius:6px;color:#fff;padding:0 10px;margin-top:4px;" />
          </label>
          <label class="modal-field-label">Folder
            <input type="text" data-edit-folder value="${escapePageText(asset.folder || '/')}" placeholder="/heroes" style="width:100%;height:36px;background:#0d1012;border:1px solid var(--line);border-radius:6px;color:#fff;padding:0 10px;margin-top:4px;" />
          </label>
          <label class="modal-field-label">Tags (comma-separated)
            <input type="text" data-edit-tags value="${escapePageText((asset.tags || []).join(', '))}" placeholder="marketing, banner" style="width:100%;height:36px;background:#0d1012;border:1px solid var(--line);border-radius:6px;color:#fff;padding:0 10px;margin-top:4px;" />
          </label>
          <div class="focal-field-group">
            <span class="focal-field-label">Focal point (object-position)</span>
            <div class="focal-point-widget">
              <div class="focal-point-pad" data-edit-focal-pad>
                <div class="focal-crosshair-h" style="top:${focal.y}%;"></div>
                <div class="focal-crosshair-v" style="left:${focal.x}%;"></div>
                <div class="focal-point-circle" style="left:${focal.x}%;top:${focal.y}%;"></div>
              </div>
              <div class="focal-point-meta">
                <span data-edit-focal-display>X: ${focal.x}%, Y: ${focal.y}%</span>
                <button type="button" class="focal-reset-btn" data-edit-focal-center>Center (50, 50)</button>
              </div>
            </div>
          </div>
          <div class="modal-actions" style="margin-top:10px;">
            <button type="button" class="ghost-btn" data-close>Cancel</button>
            <button type="submit" class="primary-btn">Save details</button>
          </div>
        </form>
      </section>
    `;

    document.body.append(backdrop);
    const close = () => {
      release();
      backdrop.remove();
    };
    const release = bindDialog(backdrop, close);
    backdrop.querySelectorAll('[data-close]').forEach((btn) => btn.addEventListener('click', close));

    const pad = backdrop.querySelector<HTMLElement>('[data-edit-focal-pad]');
    const disp = backdrop.querySelector<HTMLElement>('[data-edit-focal-display]');
    const circle = backdrop.querySelector<HTMLElement>('.focal-point-circle');
    const crossH = backdrop.querySelector<HTMLElement>('.focal-crosshair-h');
    const crossV = backdrop.querySelector<HTMLElement>('.focal-crosshair-v');
    const centerBtn = backdrop.querySelector<HTMLButtonElement>('[data-edit-focal-center]');
    let focalX = focal.x, focalY = focal.y;

    const setPos = (x: number, y: number) => {
      focalX = Math.max(0, Math.min(100, Math.round(x)));
      focalY = Math.max(0, Math.min(100, Math.round(y)));
      if (disp) disp.textContent = `X: ${focalX}%, Y: ${focalY}%`;
      if (circle) { circle.style.left = `${focalX}%`; circle.style.top = `${focalY}%`; }
      if (crossH) crossH.style.top = `${focalY}%`;
      if (crossV) crossV.style.left = `${focalX}%`;
    };

    if (pad) {
      let isDragging = false;
      const handlePointer = (e: PointerEvent) => {
        const rect = pad.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        setPos(((e.clientX - rect.left) / rect.width) * 100, ((e.clientY - rect.top) / rect.height) * 100);
      };
      pad.addEventListener('pointerdown', (e) => {
        isDragging = true;
        pad.setPointerCapture(e.pointerId);
        handlePointer(e);
      });
      pad.addEventListener('pointermove', (e) => { if (isDragging) handlePointer(e); });
      const stopDrag = (e: PointerEvent) => {
        if (isDragging) {
          isDragging = false;
          try { pad.releasePointerCapture(e.pointerId); } catch (_err) { void _err; }
        }
      };
      pad.addEventListener('pointerup', stopDrag);
      pad.addEventListener('pointercancel', stopDrag);
    }

    centerBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      setPos(50, 50);
    });

    const form = backdrop.querySelector<HTMLFormElement>('[data-edit-asset-form]')!;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const nameInput = form.querySelector<HTMLInputElement>('[data-edit-name]')!;
      const altInput = form.querySelector<HTMLInputElement>('[data-edit-alt]')!;
      const captionInput = form.querySelector<HTMLInputElement>('[data-edit-caption]')!;
      const folderInput = form.querySelector<HTMLInputElement>('[data-edit-folder]')!;
      const tagsInput = form.querySelector<HTMLInputElement>('[data-edit-tags]')!;

      asset.name = nameInput.value.trim() || asset.name;
      asset.altText = altInput.value;
      asset.caption = captionInput.value;
      asset.folder = normalizeFolderPath(folderInput.value.trim() || '/');
      asset.tags = tagsInput.value.split(',').map((t) => t.trim()).filter(Boolean);
      asset.focalPoint = { x: focalX, y: focalY };
      const frameDoc = this.adapter.getFrameDocument();
      if (frameDoc) {
        frameDoc.querySelectorAll<HTMLImageElement>('img').forEach((img) => {
          const aid = img.getAttribute('data-asset-id');
          const alt = img.getAttribute('alt');
          const src = img.getAttribute('src');
          if (
            aid === asset.id ||
            (alt && (alt === asset.name || alt === asset.altText)) ||
            (src && (src.includes(asset.id) || src.includes(asset.name)))
          ) {
            img.style.objectPosition = String(focalX) + '% ' + String(focalY) + '%';
          }
        });
      }
      const traverseComponents = (comp: Component) => {
        const attrs = (comp.getAttributes?.() ?? {}) as Record<string, string>;
        const aid = attrs['data-asset-id'];
        const alt = attrs.alt;
        const src = attrs.src;
        if (
          aid === asset.id ||
          (alt && (alt === asset.name || alt === asset.altText)) ||
          (src && (src.includes(asset.id) || src.includes(asset.name)))
        ) {
          const posStr = String(focalX) + '% ' + String(focalY) + '%';
          comp.addStyle({ 'object-position': posStr });
          const curStyle = attrs.style || '';
          const sObj: Record<string, string> = {};
          if (curStyle) {
            for (const p of curStyle.split(';')) {
              const [k, v] = p.split(':').map((s) => s.trim());
              if (k && v) sObj[k] = v;
            }
          }
          sObj['object-position'] = posStr;
          comp.addAttributes({
            style: Object.entries(sObj).map(([k, v]) => `${k}:${v}`).join(';'),
          });
        }
        const children = comp.components?.()?.models ?? [];
        for (const child of children) traverseComponents(child);
      };
      const root = this.adapter.getRoot();
      if (root) traverseComponents(root);

      this.saveNow('Asset details updated');
      this.renderAssetList();
      if (this.selected) this.onSelect(this.selected);
      this.toast('Asset updated', `Saved metadata for "${asset.name}".`);
      close();
    });
  }

  private renderImageInspector(host: HTMLElement, component: Component): void {
    const attrs = (component.getAttributes?.() ?? {}) as Record<string, string>;
    const styles = (component.getStyle?.() ?? {}) as Record<string, string>;
    const currentAssetId = attrs['data-asset-id'];
    const currentAsset = (this.project.assets || []).find((a) => a.id === currentAssetId);
    const src = attrs.src || currentAsset?.url || '';
    const alt = attrs.alt || currentAsset?.altText || '';
    const fit = styles['object-fit'] || 'cover';

    let posX = 50, posY = 50;
    if (styles['object-position']) {
      const parts = styles['object-position'].split(/\s+/).map((p) => Number.parseInt(p, 10));
      if (parts.length >= 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
        posX = parts[0];
        posY = parts[1];
      }
    } else if (currentAsset?.focalPoint) {
      posX = currentAsset.focalPoint.x;
      posY = currentAsset.focalPoint.y;
    }

    const box = document.createElement('div');
    box.className = 'field-group image-inspector-group';

    const title = document.createElement('strong');
    title.className = 'field-group-title';
    title.textContent = 'Image settings';
    box.append(title);

    const pickBtn = document.createElement('button');
    pickBtn.type = 'button';
    pickBtn.className = 'primary-btn';
    pickBtn.style.width = '100%';
    pickBtn.style.marginBottom = '6px';
    pickBtn.textContent = currentAsset ? `Change asset (${currentAsset.name})` : 'Choose from asset library';
    pickBtn.onclick = () => {
      this.openAssetPickerModal((asset) => {
        this.applyAssetToComponent(component, asset);
      });
    };
    box.append(pickBtn);

    if (src) {
      const previewBox = document.createElement('div');
      previewBox.className = 'image-preview-box';
      const img = document.createElement('img');
      img.src = src;
      img.alt = alt;
      img.style.objectFit = fit;
      img.style.objectPosition = String(posX) + '% ' + String(posY) + '%';
      previewBox.append(img);
      box.append(previewBox);
    }

    const srcLabel = document.createElement('label');
    srcLabel.className = 'field dynamic-field';
    srcLabel.textContent = 'Image URL';
    const srcInput = document.createElement('input');
    srcInput.type = 'text';
    srcInput.value = src;
    srcInput.placeholder = 'https://...';
    srcInput.addEventListener('input', () => {
      component.addAttributes({ src: srcInput.value });
      this.adapter.updateComponentTraits(component, { src: srcInput.value });
      this.scheduleSave('Image source updated');
    });
    srcLabel.append(srcInput);
    box.append(srcLabel);

    const altLabel = document.createElement('label');
    altLabel.className = 'field dynamic-field';
    altLabel.textContent = 'Alt text (accessibility & SEO)';
    const altInput = document.createElement('input');
    altInput.type = 'text';
    altInput.value = alt;
    altInput.placeholder = 'Describe the image';
    altInput.addEventListener('input', () => {
      component.addAttributes({ alt: altInput.value });
      this.adapter.updateComponentTraits(component, { alt: altInput.value });
      if (currentAsset) currentAsset.altText = altInput.value;
      this.scheduleSave('Image alt text updated');
    });
    altLabel.append(altInput);
    box.append(altLabel);

    const getLiveElement = (): HTMLElement | null => {
      const frameDoc = this.adapter.getFrameDocument();
      const cid = component.getId?.();
      return (
        (component.view?.el as HTMLElement | undefined) ??
        ((component.getView?.() as { el?: HTMLElement })?.el as HTMLElement | undefined) ??
        (cid && frameDoc ? (frameDoc.getElementById(cid) as HTMLElement | null) : null) ??
        (frameDoc ? (frameDoc.querySelector(`img[src="${src}"]`) as HTMLElement | null) : null)
      );
    };

    const updateInlineStyles = (extra: Record<string, string>) => {
      const curStyles = (component.getStyle?.() ?? {}) as Record<string, string>;
      const activeHeight = heightInput?.value?.trim() || curStyles.height;
      const mergedStyles: Record<string, string> = {
        ...curStyles,
        ...extra,
      };
      if (activeHeight && activeHeight !== 'auto') {
        mergedStyles.height = activeHeight;
      } else if (activeHeight === 'auto') {
        delete mergedStyles.height;
      }
      if (fitSelect?.value) {
        mergedStyles['object-fit'] = fitSelect.value;
      }
      component.addStyle(mergedStyles);
      const styleStr = Object.entries(mergedStyles)
        .filter(([k, v]) => k && v)
        .map(([k, v]) => `${k}:${v}`)
        .join(';');
      component.addAttributes({ style: styleStr });
    };

    let initHeight = (styles.height && styles.height !== 'auto' ? styles.height : '') || '';
    if (!initHeight && attrs.style) {
      for (const p of attrs.style.split(';')) {
        const [k, v] = p.split(':').map((s) => s.trim());
        if (k === 'height' && v && v !== 'auto') initHeight = v;
      }
    }
    if (!initHeight) {
      const el = getLiveElement();
      if (el?.style.height && el.style.height !== 'auto') initHeight = el.style.height;
    }
    const heightLabel = document.createElement('label');
    heightLabel.className = 'field dynamic-field';
    heightLabel.textContent = 'Frame height (enables cropping)';
    const heightInput = document.createElement('input');
    heightInput.type = 'text';
    heightInput.value = initHeight;
    heightInput.placeholder = 'e.g. 280px, 350px, auto';
    heightInput.addEventListener('input', () => {
      const val = heightInput.value.trim();
      if (val && val !== 'auto') {
        component.addStyle({ height: val });
        updateInlineStyles({ height: val });
        const el = getLiveElement();
        if (el) el.style.height = val;
      } else {
        const curStyles = (component.getStyle?.() ?? {}) as Record<string, string>;
        delete curStyles.height;
        component.setStyle(curStyles);
        updateInlineStyles({ height: 'auto' });
        const el = getLiveElement();
        if (el) el.style.removeProperty('height');
      }
      this.scheduleSave('Image height updated');
    });
    heightLabel.append(heightInput);
    box.append(heightLabel);

    const fitLabel = document.createElement('label');
    fitLabel.className = 'field dynamic-field';
    fitLabel.textContent = 'Display fit (object-fit)';
    const fitSelect = document.createElement('select');
    ['cover', 'contain', 'fill', 'none', 'scale-down'].forEach((m) => {
      const opt = document.createElement('option');
      opt.value = m;
      opt.textContent = m;
      opt.selected = m === fit;
      fitSelect.append(opt);
    });
    fitSelect.addEventListener('change', () => {
      const activeHeight = heightInput?.value?.trim();
      component.addStyle({ 'object-fit': fitSelect.value });
      updateInlineStyles({ 'object-fit': fitSelect.value });
      const el = getLiveElement();
      if (el) {
        el.style.objectFit = fitSelect.value;
        if (activeHeight && activeHeight !== 'auto') {
          el.style.height = activeHeight;
        }
      }
      const img = box.querySelector<HTMLImageElement>('.image-preview-box img');
      if (img) img.style.objectFit = fitSelect.value;
      this.saveNow('Image fit updated');
    });
    fitLabel.append(fitSelect);
    box.append(fitLabel);

    const focalWrap = document.createElement('div');
    focalWrap.className = 'focal-field-group';
    focalWrap.innerHTML = `
      <span class="focal-field-label">Focal point (object-position)</span>
      <div class="focal-point-widget">
        <div class="focal-point-pad" data-inspector-focal-pad>
          <div class="focal-crosshair-h" style="top:${posY}%;"></div>
          <div class="focal-crosshair-v" style="left:${posX}%;"></div>
          <div class="focal-point-circle" style="left:${posX}%;top:${posY}%;"></div>
        </div>
        <div class="focal-point-meta">
          <span data-inspector-focal-display>X: ${posX}%, Y: ${posY}%</span>
          <button type="button" class="focal-reset-btn" data-inspector-focal-center>Center (50, 50)</button>
        </div>
      </div>
    `;
    const pad = focalWrap.querySelector<HTMLElement>('[data-inspector-focal-pad]');
    const disp = focalWrap.querySelector<HTMLElement>('[data-inspector-focal-display]');
    const circle = focalWrap.querySelector<HTMLElement>('.focal-point-circle');
    const crossH = focalWrap.querySelector<HTMLElement>('.focal-crosshair-h');
    const crossV = focalWrap.querySelector<HTMLElement>('.focal-crosshair-v');
    const centerBtn = focalWrap.querySelector<HTMLButtonElement>('[data-inspector-focal-center]');

    const setPosition = (x: number, y: number) => {
      const cx = Math.max(0, Math.min(100, Math.round(x)));
      const cy = Math.max(0, Math.min(100, Math.round(y)));
      if (disp) disp.textContent = `X: ${cx}%, Y: ${cy}%`;
      if (circle) { circle.style.left = `${cx}%`; circle.style.top = `${cy}%`; }
      if (crossH) crossH.style.top = `${cy}%`;
      if (crossV) crossV.style.left = `${cx}%`;
      const pos = `${cx}% ${cy}%`;
      const activeHeight = heightInput?.value?.trim();
      component.addStyle({ 'object-position': pos });
      updateInlineStyles({ 'object-position': pos });
      const el = getLiveElement();
      if (el) {
        el.style.objectPosition = pos;
        if (activeHeight && activeHeight !== 'auto') {
          el.style.height = activeHeight;
        }
      }
      const img = box.querySelector<HTMLImageElement>('.image-preview-box img');
      if (img) {
        img.style.objectPosition = pos;
      }
      if (currentAsset) currentAsset.focalPoint = { x: cx, y: cy };
      this.scheduleSave('Image focal point updated');
    };

    if (pad) {
      let isDragging = false;
      const handlePointer = (e: PointerEvent) => {
        const rect = pad.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        setPosition(((e.clientX - rect.left) / rect.width) * 100, ((e.clientY - rect.top) / rect.height) * 100);
      };
      pad.addEventListener('pointerdown', (e) => {
        isDragging = true;
        pad.setPointerCapture(e.pointerId);
        handlePointer(e);
      });
      pad.addEventListener('pointermove', (e) => { if (isDragging) handlePointer(e); });
      const stopDrag = (e: PointerEvent) => {
        if (isDragging) {
          isDragging = false;
          try { pad.releasePointerCapture(e.pointerId); } catch (_err) { void _err; }
          this.saveNow('Image focal point updated');
        }
      };
      pad.addEventListener('pointerup', stopDrag);
      pad.addEventListener('pointercancel', stopDrag);
    }

    centerBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      setPosition(50, 50);
      this.saveNow('Image focal point updated');
    });

    box.append(focalWrap);
    host.append(box);
  }

  private bindPages(): void {
    document
      .querySelector('#pagesPanel .mini-btn')
      ?.addEventListener('click', () => this.createPage());
    document.querySelector('#pagesPanel')?.addEventListener('click', async (event) => {
      const menuButton = (event.target as HTMLElement).closest<HTMLButtonElement>(
        '[data-menu-action]',
      );
      if (menuButton) {
        const menus = ensureMenus(this.project);
        const select = document.querySelector<HTMLSelectElement>('[data-menu-select]');
        const menu = menus.find((item) => item.id === (select?.value ?? menus[0].id)) ?? menus[0];
        const action = menuButton.dataset.menuAction;
        if (action === 'new') {
          const name = (await askText('Menu name', 'Footer menu'))?.trim();
          if (name) menus.push({ id: crypto.randomUUID(), name, items: [], mobileMode: 'drawer' });
        } else if (action === 'add') {
          this.openMenuLinkSettings(menu);
          return;
        } else {
          const row = menuButton.closest<HTMLElement>('[data-menu-item]');
          const itemId = row?.dataset.menuItem;
          if (itemId && action === 'delete') removeMenuItem(menu, itemId);
          if (itemId && action === 'up') moveMenuItem(menu, itemId, -1);
          if (itemId && action === 'down') moveMenuItem(menu, itemId, 1);
          if (itemId && action === 'edit') {
            const item = findMenuItem(menu, itemId);
            if (item) this.openMenuLinkSettings(menu, item);
            return;
          }
        }
        this.renderMenus(
          document.querySelector('#pagesPanel')!,
          document.querySelector('#siteTree')!,
        );
        this.scheduleSave('Menu updated');
        return;
      }
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-page-action]');
      if (!button) return;
      const page = this.project.pages.find((item) => item.id === button.dataset.pageId);
      if (!page) return;
      const action = button.dataset.pageAction;
      if (action === 'open') this.switchPage(page.id);
      if (action === 'home') {
        setHomepage(this.project, page.id);
        this.renderPages();
        this.saveNow('Homepage changed');
      }
      if (action === 'duplicate') {
        const copy = duplicatePage(this.project, page);
        this.includePageInDefaultMenu(copy);
        this.renderPages();
        this.switchPage(copy.id);
      }
      if (['rename', 'parent', 'folder', 'seo'].includes(action ?? '')) {
        this.openPageSettings(page);
        return;
      }
      if (action === 'navigation') {
        page.settings = {
          ...(page.settings ?? { showInNavigation: true, passwordProtected: false }),
          showInNavigation: page.settings?.showInNavigation === false,
        };
        this.renderPages();
        this.saveNow('Navigation visibility changed');
      }
      if (action === 'protect') {
        page.settings = {
          ...(page.settings ?? { showInNavigation: true, passwordProtected: false }),
          passwordProtected: page.settings?.passwordProtected !== true,
        };
        this.renderPages();
        this.saveNow('Page protection changed');
      }
      if (action === 'permissions') {
        void this.openPagePermissionsModal(page);
        return;
      }
      if (action === 'delete') this.deletePage(page.id);
      if (action === 'up' || action === 'down') this.reorderPage(page.id, action === 'up' ? -1 : 1);
      if (action === 'restore') {
        restorePage(this.project, page.id);
        this.renderPages();
        this.saveNow('Page restored');
      }
    });
    document.querySelector('#pagesPanel')?.addEventListener('dragstart', (event) => {
      const row = (event.target as HTMLElement).closest<HTMLElement>('.page-row[data-page-id]');
      if (!row) return;
      this.draggedPageId = row.dataset.pageId ?? null;
      row.classList.add('dragging');
      (event as DragEvent).dataTransfer?.setData('text/plain', this.draggedPageId ?? '');
    });
    document.querySelector('#pagesPanel')?.addEventListener('dragover', (event) => {
      const row = (event.target as HTMLElement).closest<HTMLElement>('.page-row[data-page-id]');
      if (!row || !this.draggedPageId || row.dataset.pageId === this.draggedPageId) return;
      event.preventDefault();
      document
        .querySelectorAll('.page-row.drop-target')
        .forEach((item) => item.classList.remove('drop-target'));
      row.classList.add('drop-target');
    });
    document.querySelector('#pagesPanel')?.addEventListener('drop', (event) => {
      const row = (event.target as HTMLElement).closest<HTMLElement>('.page-row[data-page-id]');
      const targetId = row?.dataset.pageId;
      if (!targetId || !this.draggedPageId || targetId === this.draggedPageId) return;
      event.preventDefault();
      this.movePageBefore(this.draggedPageId, targetId);
      this.draggedPageId = null;
      this.renderPages();
      this.saveNow('Pages reordered');
    });
    document.querySelector('#pagesPanel')?.addEventListener('dragend', () => {
      this.draggedPageId = null;
      document
        .querySelectorAll('.page-row.dragging, .page-row.drop-target')
        .forEach((item) => item.classList.remove('dragging', 'drop-target'));
    });
  }

  private bindInspector(): void {
    const spacing = document.querySelector<HTMLInputElement>('#spacingRange');
    spacing?.addEventListener('input', () => {
      if (!this.selected) return;
      this.adapter.updateStyles(this.selected, {
        'padding-top': `${spacing.value}px`,
        'padding-bottom': `${spacing.value}px`,
      });
      document
        .querySelector('#spacingValue')
        ?.replaceChildren(document.createTextNode(`${spacing.value} px`));
    });
    document
      .querySelector('#bgColor')
      ?.addEventListener('input', (event) =>
        this.updateColor('background-color', (event.target as HTMLInputElement).value, '#bgValue'),
      );
    document
      .querySelector('#textColor')
      ?.addEventListener('input', (event) =>
        this.updateColor('color', (event.target as HTMLInputElement).value, '#textValue'),
      );
    document.querySelectorAll<HTMLInputElement>('.toggle-row input').forEach((toggle, index) =>
      toggle.addEventListener('change', () => {
        if (!this.selected) return;
        const device = index === 0 ? 'desktop' : 'mobile';
        this.adapter.updateStyles(this.selected as Component, {
          [`display-${device}`]: toggle.checked ? 'block' : 'none',
        });
      }),
    );
    document.querySelectorAll<HTMLButtonElement>('.inspector-tabs button').forEach((tab) =>
      tab.addEventListener('click', async () => {
        document
          .querySelectorAll('.inspector-tabs button')
          .forEach((item) => item.classList.remove('active'));
        tab.classList.add('active');
        const tabName = (tab.dataset.inspectorTab || tab.textContent || '').toLowerCase().trim();
        const propertyGroups = document.querySelectorAll<HTMLElement>(
          '.inspector .property-group, .inspector > .outline-btn',
        );
        propertyGroups.forEach((group) => {
          group.style.display = tabName === 'style' ? '' : 'none';
        });
        this.renderDynamicInspector(tabName);
      }),
    );
  }

  private bindLayers(): void {
    document.querySelector('#collapseLayers')?.addEventListener('click', async () => {
      document.querySelector('#layerList')?.classList.toggle('is-collapsed');
    });
    document.querySelector('#layerList')?.addEventListener('click', async (event) => {
      const target = event.target as HTMLElement;
      const action = target.closest<HTMLButtonElement>('[data-layer-action]');
      const row = target.closest<HTMLElement>('[data-component-id]');
      if (!row) return;
      const component = this.findById(row.dataset.componentId ?? '');
      if (!component) return;
      if (action) {
        this.layerAction(action.dataset.layerAction ?? '', component);
        return;
      }
      if ((target as HTMLElement).closest('[data-layer-toggle]')) {
        this.toggleLayer(component.getId());
        return;
      }
      this.adapter.selectComponent(component);
    });
    document.querySelector('#layerList')?.addEventListener('dragstart', (event) => {
      const row = (event.target as HTMLElement).closest<HTMLElement>('[data-component-id]');
      if (row) this.draggedLayerId = row.dataset.componentId ?? null;
    });
    document.querySelector('#layerList')?.addEventListener('dragover', (event) => {
      const row = (event.target as HTMLElement).closest<HTMLElement>('[data-component-id]');
      if (!row || !this.draggedLayerId) return;
      const target = this.findById(row.dataset.componentId ?? '');
      const dragged = this.findById(this.draggedLayerId);
      if (!target || !dragged || !this.canDrop(dragged, target)) return;
      event.preventDefault();
      row.dataset.dropValid = 'true';
    });
    document.querySelector('#layerList')?.addEventListener('drop', (event) => {
      const row = (event.target as HTMLElement).closest<HTMLElement>('[data-component-id]');
      const target = row && this.findById(row.dataset.componentId ?? '');
      const dragged = this.draggedLayerId && this.findById(this.draggedLayerId);
      if (target && dragged && this.canDrop(dragged, target)) {
        event.preventDefault();
        dragged.move(target, { at: target.components().length });
        this.scheduleSave();
      }
      this.draggedLayerId = null;
    });
  }

  private addBlock(type: string): void {
    this.addDefinition(blockMap[type] ?? type);
    try {
      const recent = JSON.parse(localStorage.getItem('webkiln-recent-blocks') ?? '[]') as string[];
      localStorage.setItem(
        'webkiln-recent-blocks',
        JSON.stringify([type, ...recent.filter((item) => item !== type)].slice(0, 8)),
      );
    } catch {
      /* Optional preference. */
    }
  }

  private addDefinition(type: string): void {
    const definition = getComponentDefinition(type);
    if (!definition) return;
    const parent = this.selected && this.isContainer(this.selected) ? this.selected : undefined;
    const created = firstComponent(this.adapter.addComponent(definition.defaultContent, parent));
    if (created) {
      created.set({ name: definition.displayName, type: definition.id });
      this.adapter.selectComponent(created);
      this.toast(`${definition.displayName} added`, 'The new component is selected on the canvas.');
    }
  }

  private isContainer(component: Component): boolean {
    const tag = String(component.get('tagName') || '').toLowerCase();
    return ['body', 'div', 'section', 'main', 'header', 'footer', 'form'].includes(tag);
  }

  private onSelect(component: Component | null): void {
    this.selected = component;
    const label = componentLabel(component);
    document.querySelector('#inspectorTitle')?.replaceChildren(document.createTextNode(label));
    document.querySelector('#selectionLabel')?.replaceChildren(document.createTextNode(label));
    document.querySelector('#elementPath')?.replaceChildren(document.createTextNode(label));
    document
      .querySelector('#selectionStatus')
      ?.replaceChildren(document.createTextNode(`${label} selected`));
    this.renderSelectionBreadcrumbs(component);
    this.syncBasicInspector(component);
    this.renderLayers();
    this.renderDynamicInspector('content');
    this.updateCommentTargetPill(component);
  }

  private renderSelectionBreadcrumbs(component: Component | null): void {
    let nav = document.querySelector<HTMLElement>('#canvasBreadcrumbs');
    if (!nav) {
      nav = document.createElement('nav');
      nav.id = 'canvasBreadcrumbs';
      nav.className = 'canvas-breadcrumbs';
      nav.setAttribute('aria-label', 'Selected element hierarchy');
      document.querySelector('.workspace-bar')?.after(nav);
    }
    const chain: Component[] = [];
    let current = component;
    while (current && chain.length < 30) {
      chain.unshift(current);
      current = current.parent() ?? null;
    }
    nav.replaceChildren();
    const page = document.createElement('span');
    page.textContent =
      this.project.pages.find((item) => item.id === this.project.currentPageId)?.name ?? 'Page';
    nav.append(page);
    chain.forEach((item, index) => {
      const separator = document.createElement('span');
      separator.textContent = '›';
      separator.setAttribute('aria-hidden', 'true');
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = componentLabel(item);
      button.onclick = () => this.adapter.selectComponent(item);
      if (index === chain.length - 1) button.setAttribute('aria-current', 'true');
      nav!.append(separator, button);
    });
    if (!component) {
      const hint = document.createElement('span');
      hint.textContent = 'Select an element on the canvas';
      nav.append(hint);
    }
    const inspectorPath = document.querySelector('.element-path');
    if (inspectorPath) {
      inspectorPath.replaceChildren();
      const text = document.createElement('span');
      text.id = 'elementPath';
      text.textContent = [page.textContent, ...chain.map(componentLabel)].join(' › ');
      inspectorPath.append(text);
    }
  }

  private syncBasicInspector(component: Component | null): void {
    if (!component) return;
    const styles = component.getStyle() as Record<string, string>;
    const bg = styles['background-color'] || '#0b0d0f';
    const color = styles.color || '#f7f7f5';
    const bgInput = document.querySelector<HTMLInputElement>('#bgColor');
    const textInput = document.querySelector<HTMLInputElement>('#textColor');
    if (bgInput) bgInput.value = this.toHex(bg);
    if (textInput) textInput.value = this.toHex(color);
    document.querySelector('#bgValue')?.replaceChildren(document.createTextNode(bg.toUpperCase()));
    document
      .querySelector('#textValue')
      ?.replaceChildren(document.createTextNode(color.toUpperCase()));
    const spacing = Number.parseInt(styles['padding-top'] || '60', 10);
    const spacingInput = document.querySelector<HTMLInputElement>('#spacingRange');
    if (spacingInput) spacingInput.value = String(Number.isNaN(spacing) ? 60 : spacing);
    document
      .querySelector('#spacingValue')
      ?.replaceChildren(document.createTextNode(`${spacing || 60} px`));
  }

  private renderDynamicInspector(tab: string): void {
    let host = document.querySelector<HTMLElement>('#dynamicInspector');
    if (!host) {
      host = document.createElement('div');
      host.id = 'dynamicInspector';
      const pathEl = document.querySelector('.element-path');
      if (pathEl && pathEl.parentNode) {
        pathEl.parentNode.insertBefore(host, pathEl.nextSibling);
      } else {
        document.querySelector('.inspector')?.append(host);
      }
    }
    const propertyGroups = document.querySelectorAll<HTMLElement>(
      '.inspector .property-group, .inspector > .outline-btn',
    );
    propertyGroups.forEach((group) => {
      group.style.display = !this.contentOnlyMode && tab === 'style' ? '' : 'none';
    });
    host.replaceChildren();
    if (!this.selected) return;

    if (this.contentOnlyMode) {
      const tagName = String(this.selected.get('tagName') || '').toLowerCase();
      const isImg = tagName === 'img' || Boolean(this.selected.getAttributes?.()?.['src']);
      const isText =
        ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'a', 'button', 'li', 'label'].includes(
          tagName,
        ) ||
        Boolean(this.selected.get('content')) ||
        Boolean(this.selected.getAttributes?.()?.['href']);

      if (isImg) {
        this.renderImageInspector(host, this.selected);
      } else if (isText) {
        this.renderContentControls(host);
      } else {
        const notice = document.createElement('div');
        notice.className = 'content-mode-notice';
        notice.innerHTML = `
          <strong>Élément structurel</strong>
          <p>La structure et la mise en page de ce conteneur sont verrouillées par l'équipe design. Cliquez directement sur un texte ou une image pour modifier son contenu.</p>
        `;
        host.append(notice);
      }
      return;
    }

    this.renderSmartSectionInspector(host);
    this.renderFormBinding(host);
    if (tab === 'content') this.renderContentControls(host);
    if (tab === 'advanced') this.renderAdvancedControls(host);
  }

  private renderFormBinding(host: HTMLElement): void {
    if (!this.selected || String(this.selected.get('tagName') || '').toLowerCase() !== 'form')
      return;
    const attributes = (this.selected.getAttributes?.() ?? {}) as Record<string, string>;
    const card = document.createElement('section');
    card.className = 'smart-inspector';
    const eyebrow = document.createElement('p');
    eyebrow.className = 'eyebrow';
    eyebrow.textContent = 'WebKiln form';
    const title = document.createElement('strong');
    title.textContent = 'Connect this form to submissions';
    const note = document.createElement('p');
    note.className = 'panel-note';
    note.textContent = this.forms.length
      ? 'Only active forms from this site can receive public submissions.'
      : 'Create a form in the Forms dashboard, then return here to connect it.';
    const label = document.createElement('label');
    label.className = 'field dynamic-field';
    label.textContent = 'Submission form';
    const select = document.createElement('select');
    const empty = document.createElement('option');
    empty.value = '';
    empty.textContent = 'Not connected';
    select.append(empty);
    this.forms.forEach((form) => {
      const option = document.createElement('option');
      option.value = form.id;
      option.textContent = `${form.name} · ${form.fields.length} fields`;
      option.selected = attributes['data-wk-form-id'] === form.id;
      select.append(option);
    });
    select.addEventListener('change', () => {
      const formId = select.value;
      if (formId) {
        this.selected?.addAttributes({
          'data-wk-form-id': formId,
          method: 'post',
          action: `/api/forms/${encodeURIComponent(formId)}/submit`,
        });
        this.toast(
          'Form connected',
          'Public submissions will use WebKiln validation and D1 storage.',
        );
      } else {
        this.selected?.removeAttributes('data-wk-form-id');
        this.selected?.removeAttributes('action');
        this.selected?.removeAttributes('method');
        this.toast('Form disconnected', 'This form will not accept public submissions.');
      }
      this.scheduleSave();
    });
    label.append(select);
    card.append(eyebrow, title, note, label);
    host.append(card);
  }

  private renderSmartSectionInspector(host: HTMLElement): void {
    if (!this.selected) return;
    const attributes = (this.selected.getAttributes?.() ?? {}) as Record<string, string>;
    const type = attributes['data-wk-smart'] || String(this.selected.get('type') || '');
    const smart = getComponentDefinition(type)?.smartSection;
    if (!smart) return;
    const card = document.createElement('section');
    card.className = 'smart-inspector';
    const eyebrow = document.createElement('p');
    eyebrow.className = 'eyebrow';
    eyebrow.textContent = 'Smart section';
    const title = document.createElement('strong');
    title.textContent = getComponentDefinition(type)?.displayName ?? 'Smart section';
    const purpose = document.createElement('p');
    purpose.className = 'panel-note';
    purpose.textContent = smart.purpose;
    const variantLabel = document.createElement('label');
    variantLabel.className = 'field dynamic-field';
    variantLabel.textContent = 'Layout variant';
    const variant = document.createElement('select');
    smart.variants.forEach((name) => {
      const option = document.createElement('option');
      option.value = name;
      option.textContent = name.charAt(0).toUpperCase() + name.slice(1);
      option.selected = attributes['data-wk-variant'] === name;
      variant.append(option);
    });
    variantLabel.append(variant);
    variant.addEventListener('change', () => {
      this.selected?.addAttributes({ 'data-wk-variant': variant.value });
      this.scheduleSave();
      this.toast('Smart section updated', `${variant.value} layout variant applied.`);
    });
    const checklist = document.createElement('details');
    const summary = document.createElement('summary');
    summary.textContent = 'Content checklist';
    checklist.append(summary);
    const list = document.createElement('ul');
    smart.requiredContent.forEach((item) => {
      const entry = document.createElement('li');
      entry.textContent = item;
      list.append(entry);
    });
    checklist.append(list);
    card.append(eyebrow, title, purpose, variantLabel, checklist);
    if (smart.dataSource) {
      const source = document.createElement('small');
      source.textContent = `Data source: ${smart.dataSource}`;
      card.append(source);
    }
    host.append(card);
  }

  private isCarouselRoot(comp: Component): boolean {
    const classes = comp.getClasses?.() || [];
    const attrs = (comp.getAttributes?.() ?? {}) as Record<string, string>;
    const classAttr = attrs.class || '';

    if (classes.some((c: string) => c.startsWith('wk-carousel-') && c !== 'wk-carousel')) {
      return false;
    }
    if (/\bwk-carousel-(slide|track|container|image|caption|dots?|prev|next)\b/.test(classAttr)) {
      return false;
    }

    return (
      classes.includes('wk-carousel') ||
      /\bwk-carousel(?![a-zA-Z0-9_-])/.test(classAttr) ||
      attrs['data-wk-component'] === 'carousel' ||
      comp.get?.('name') === 'Carousel'
    );
  }

  private getSelectedCarousel(): Component | null {
    if (!this.selected) return null;
    let curr: Component | null = this.selected;
    while (curr) {
      if (this.isCarouselRoot(curr)) {
        return curr;
      }
      curr = curr.parent?.() ?? null;
    }
    return null;
  }

  private getCarouselSlides(carouselComp: Component): CarouselSlideData[] {
    const stored = carouselComp.get?.('carouselSlides') as CarouselSlideData[] | undefined;
    if (stored && Array.isArray(stored) && stored.length > 0) {
      return stored.map((s) => ({ ...s }));
    }

    const frameDoc = this.adapter.getFrameDocument();
    const cid = carouselComp.getId?.();
    const carouselEl = (carouselComp.view?.el ??
      (cid ? frameDoc?.getElementById(cid) : null) ??
      frameDoc?.querySelector('.wk-carousel')) as HTMLElement | null;

    let slideEls: Element[] = [];
    if (carouselEl) {
      slideEls = Array.from(carouselEl.querySelectorAll('.wk-carousel-slide'));
    }
    if (slideEls.length === 0) {
      const html =
        (typeof carouselComp.toHTML === 'function' ? carouselComp.toHTML() : '') ||
        carouselComp.getInnerHTML?.() ||
        '';
      if (html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        slideEls = Array.from(doc.querySelectorAll('.wk-carousel-slide'));
      }
    }

    if (slideEls.length > 0) {
      const parsed = slideEls.map((slide, i) => {
        const img = slide.querySelector('.wk-carousel-image, img');
        const titleEl = slide.querySelector('h3, h2, h4, .title');
        const descEl = slide.querySelector('p, .desc');
        return {
          src:
            img?.getAttribute('src') ||
            'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80',
          alt: img?.getAttribute('alt') || `Slide ${i + 1}`,
          title: titleEl?.textContent?.trim() || `Slide ${i + 1}`,
          description: descEl?.textContent?.trim() || '',
        };
      });
      carouselComp.set?.('carouselSlides', parsed);
      return parsed;
    }

    const defaults: CarouselSlideData[] = [
      {
        src: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80',
        alt: 'Serene sandy beach with turquoise ocean',
        title: 'Paradise Coast',
        description: 'Discover untouched horizons and serene shoreline retreats.',
      },
      {
        src: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=1200&q=80',
        alt: 'Snow-capped alpine mountains under star sky',
        title: 'Alpine Heights',
        description: 'Breathtaking summit vistas above the morning clouds.',
      },
      {
        src: 'https://images.unsplash.com/photo-1477959858617-67f30bc75b82?auto=format&fit=crop&w=1200&q=80',
        alt: 'Vibrant cityscape at sunset',
        title: 'Metropolitan Glow',
        description: 'The energy and modern architectural elegance of nightfall.',
      },
    ];
    carouselComp.set?.('carouselSlides', defaults);
    return defaults;
  }

  private buildCarouselMarkup(slides: CarouselSlideData[], activeIdx: number): string {
    const safeIdx = Math.max(0, Math.min(slides.length - 1, activeIdx));
    const slidesHtml = slides
      .map((slide, i) => {
        const isActive = i === safeIdx;
        return `<div class="wk-carousel-slide${isActive ? ' active' : ''}" role="group" aria-roledescription="slide" aria-label="Slide ${i + 1} of ${slides.length}" data-slide-index="${i}"${isActive ? '' : ' hidden'}><img class="wk-carousel-image" src="${slide.src}" alt="${slide.alt || slide.title || `Slide ${i + 1}`}" loading="lazy" /><div class="wk-carousel-caption"><h3>${slide.title}</h3><p>${slide.description}</p></div></div>`;
      })
      .join('');

    const dotsHtml = slides
      .map((_, i) => {
        const isActive = i === safeIdx;
        return `<button type="button" role="tab" class="wk-carousel-dot${isActive ? ' active' : ''}" aria-selected="${isActive ? 'true' : 'false'}" aria-label="Slide ${i + 1}" data-slide-to="${i}"></button>`;
      })
      .join('');

    return `<div class="wk-carousel-container"><div class="wk-carousel-track" aria-live="polite">${slidesHtml}</div><button type="button" class="wk-carousel-prev" aria-label="Previous slide">‹</button><button type="button" class="wk-carousel-next" aria-label="Next slide">›</button><div class="wk-carousel-dots" role="tablist" aria-label="Carousel pagination">${dotsHtml}</div></div>`;
  }

  private updateCarouselComponent(
    carouselComp: Component,
    slides: CarouselSlideData[],
    activeIdx: number,
  ): void {
    carouselComp.set?.('carouselSlides', slides.map((s) => ({ ...s })));
    const markup = this.buildCarouselMarkup(slides, activeIdx);
    carouselComp.components(markup);
    this.selected = carouselComp;
    const frameDoc = this.adapter.getFrameDocument();
    const cid = carouselComp.getId?.();
    const carouselEl = (carouselComp.view?.el ??
      (cid ? frameDoc?.getElementById(cid) : null) ??
      (cid ? frameDoc?.querySelector(`[id="${cid}"]`) : null) ??
      frameDoc?.querySelector('.wk-carousel')) as HTMLElement | null;
    if (carouselEl) {
      carouselEl.innerHTML = markup;
      switchCarouselSlide(carouselEl, activeIdx);
    }
    this.syncInteractionRuntime();
    try {
      this.adapter.getEditor()?.trigger('component:update', carouselComp);
    } catch {
      /* optional trigger */
    }
    this.scheduleSave('Carousel updated');
  }

  private renderCarouselControls(host: HTMLElement, carouselComp: Component): void {
    host.replaceChildren();
    const slides = this.getCarouselSlides(carouselComp);
    if (this.selected && this.selected !== carouselComp) {
      const selEl = (this.selected.view?.el ?? null) as HTMLElement | null;
      const slideEl = selEl?.closest?.('.wk-carousel-slide');
      const slideIdxAttr = slideEl?.getAttribute('data-slide-index');
      if (slideIdxAttr !== null && slideIdxAttr !== undefined) {
        const clickedIdx = parseInt(slideIdxAttr, 10);
        if (!Number.isNaN(clickedIdx) && clickedIdx >= 0 && clickedIdx < slides.length) {
          this.activeCarouselSlideIndex = clickedIdx;
        }
      }
    }
    if (this.activeCarouselSlideIndex >= slides.length) {
      this.activeCarouselSlideIndex = Math.max(0, slides.length - 1);
    }
    const activeIdx = this.activeCarouselSlideIndex;
    const currentSlide = slides[activeIdx] || slides[0];

    const box = document.createElement('div');
    box.className = 'field-group carousel-settings-group';

    const header = document.createElement('div');
    header.className = 'carousel-settings-header';
    const title = document.createElement('span');
    title.className = 'carousel-settings-title';
    title.textContent = 'Carousel Slides';
    header.append(title);
    box.append(header);

    const tabsContainer = document.createElement('div');
    tabsContainer.className = 'carousel-slide-tabs';

    slides.forEach((_, idx) => {
      const tabBtn = document.createElement('button');
      tabBtn.type = 'button';
      tabBtn.className = `carousel-slide-tab${idx === activeIdx ? ' active' : ''}`;
      tabBtn.textContent = `Slide ${idx + 1}`;
      tabBtn.addEventListener('click', () => {
        this.activeCarouselSlideIndex = idx;
        const frameDoc = this.adapter.getFrameDocument();
        const cid = carouselComp.getId?.();
        const carouselEl = (carouselComp.view?.el ??
          (cid ? frameDoc?.getElementById(cid) : null) ??
          frameDoc?.querySelector('.wk-carousel')) as HTMLElement | null;
        if (carouselEl) {
          switchCarouselSlide(carouselEl, idx);
        }
        this.renderDynamicInspector('content');
      });
      tabsContainer.append(tabBtn);
    });

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'carousel-add-slide-btn';
    addBtn.textContent = '＋ Add slide';
    addBtn.addEventListener('click', () => {
      const presets = [
        {
          src: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1200&q=80',
          title: 'Road Trip Adventure',
          desc: 'Explore new routes and untamed scenic highways.',
        },
        {
          src: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?auto=format&fit=crop&w=1200&q=80',
          title: 'Forest Sanctuary',
          desc: 'Tranquil canopy and lush natural sanctuaries.',
        },
        {
          src: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1200&q=80',
          title: 'Cascading Waters',
          desc: 'Majestic waterfalls flowing into crystal streams.',
        },
        {
          src: 'https://images.unsplash.com/photo-1518791841217-8f162f1e1131?auto=format&fit=crop&w=1200&q=80',
          title: 'Cozy Moments',
          desc: 'Warmth and calm in comfortable serene spaces.',
        },
      ];
      const nextPreset = presets[slides.length % presets.length];
      slides.push({
        src: nextPreset.src,
        alt: nextPreset.title,
        title: nextPreset.title,
        description: nextPreset.desc,
      });
      this.activeCarouselSlideIndex = slides.length - 1;
      this.updateCarouselComponent(carouselComp, slides, this.activeCarouselSlideIndex);
      this.saveNow('Carousel slide added');
      this.renderDynamicInspector('content');
    });
    tabsContainer.append(addBtn);
    box.append(tabsContainer);

    if (currentSlide) {
      const imgLabel = document.createElement('label');
      imgLabel.className = 'field dynamic-field';
      imgLabel.textContent = `Slide ${activeIdx + 1} Image URL`;
      const imgInput = document.createElement('input');
      imgInput.type = 'text';
      imgInput.value = currentSlide.src;
      imgInput.placeholder = 'https://images.unsplash.com/...';
      imgInput.addEventListener('input', () => {
        currentSlide.src = imgInput.value.trim();
        this.updateCarouselComponent(carouselComp, slides, activeIdx);
      });
      imgInput.addEventListener('change', () => {
        currentSlide.src = imgInput.value.trim();
        this.updateCarouselComponent(carouselComp, slides, activeIdx);
        this.saveNow('Carousel updated');
      });
      imgLabel.append(imgInput);
      box.append(imgLabel);

      const presetRow = document.createElement('div');
      presetRow.className = 'carousel-preset-row';
      const sampleImages = [
        {
          name: '🏖️ Beach',
          url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80',
        },
        {
          name: '🏔️ Mountain',
          url: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=1200&q=80',
        },
        {
          name: '🌆 City',
          url: 'https://images.unsplash.com/photo-1477959858617-67f30bc75b82?auto=format&fit=crop&w=1200&q=80',
        },
        {
          name: '🌲 Forest',
          url: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?auto=format&fit=crop&w=1200&q=80',
        },
        {
          name: '🚗 Road',
          url: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1200&q=80',
        },
      ];
      sampleImages.forEach((item) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'carousel-preset-pill';
        pill.textContent = item.name;
        pill.addEventListener('click', () => {
          imgInput.value = item.url;
          currentSlide.src = item.url;
          this.updateCarouselComponent(carouselComp, slides, activeIdx);
          this.saveNow('Carousel updated');
          this.renderDynamicInspector('content');
        });
        presetRow.append(pill);
      });
      box.append(presetRow);

      const altLabel = document.createElement('label');
      altLabel.className = 'field dynamic-field';
      altLabel.textContent = 'Alt text';
      const altInput = document.createElement('input');
      altInput.type = 'text';
      altInput.value = currentSlide.alt;
      altInput.placeholder = 'Image description';
      altInput.addEventListener('input', () => {
        currentSlide.alt = altInput.value;
        this.updateCarouselComponent(carouselComp, slides, activeIdx);
      });
      altInput.addEventListener('change', () => {
        currentSlide.alt = altInput.value;
        this.updateCarouselComponent(carouselComp, slides, activeIdx);
        this.saveNow('Carousel updated');
      });
      altLabel.append(altInput);
      box.append(altLabel);

      const titleLabel = document.createElement('label');
      titleLabel.className = 'field dynamic-field';
      titleLabel.textContent = 'Slide Title';
      const titleInput = document.createElement('input');
      titleInput.type = 'text';
      titleInput.value = currentSlide.title;
      titleInput.placeholder = 'Headline for this slide';
      titleInput.addEventListener('input', () => {
        currentSlide.title = titleInput.value;
        this.updateCarouselComponent(carouselComp, slides, activeIdx);
      });
      titleInput.addEventListener('change', () => {
        currentSlide.title = titleInput.value;
        this.updateCarouselComponent(carouselComp, slides, activeIdx);
        this.saveNow('Carousel updated');
      });
      titleLabel.append(titleInput);
      box.append(titleLabel);

      const descLabel = document.createElement('label');
      descLabel.className = 'field dynamic-field';
      descLabel.textContent = 'Slide Description';
      const descInput = document.createElement('textarea');
      descInput.value = currentSlide.description;
      descInput.placeholder = 'Short description';
      descInput.rows = 2;
      descInput.addEventListener('input', () => {
        currentSlide.description = descInput.value;
        this.updateCarouselComponent(carouselComp, slides, activeIdx);
      });
      descInput.addEventListener('change', () => {
        currentSlide.description = descInput.value;
        this.updateCarouselComponent(carouselComp, slides, activeIdx);
        this.saveNow('Carousel updated');
      });
      descLabel.append(descInput);
      box.append(descLabel);

      if (slides.length > 1) {
        const delBtn = document.createElement('button');
        delBtn.type = 'button';
        delBtn.className = 'carousel-delete-slide-btn';
        delBtn.textContent = `🗑️ Remove Slide ${activeIdx + 1}`;
        delBtn.addEventListener('click', () => {
          slides.splice(activeIdx, 1);
          this.activeCarouselSlideIndex = Math.max(0, activeIdx - 1);
          this.updateCarouselComponent(carouselComp, slides, this.activeCarouselSlideIndex);
          this.saveNow('Carousel slide removed');
          this.renderDynamicInspector('content');
        });
        box.append(delBtn);
      }
    }

    const navRow = document.createElement('div');
    navRow.className = 'carousel-nav-quick-row';
    const prevQuick = document.createElement('button');
    prevQuick.type = 'button';
    prevQuick.className = 'carousel-nav-quick-btn';
    prevQuick.textContent = '‹ Previous slide';
    prevQuick.addEventListener('click', () => {
      const nextIdx = (activeIdx - 1 + slides.length) % slides.length;
      this.activeCarouselSlideIndex = nextIdx;
      const frameDoc = this.adapter.getFrameDocument();
      const cid = carouselComp.getId?.();
      const carouselEl = (carouselComp.view?.el ??
        (cid ? frameDoc?.getElementById(cid) : null) ??
        frameDoc?.querySelector('.wk-carousel')) as HTMLElement | null;
      if (carouselEl) switchCarouselSlide(carouselEl, nextIdx);
      this.renderDynamicInspector('content');
    });

    const nextQuick = document.createElement('button');
    nextQuick.type = 'button';
    nextQuick.className = 'carousel-nav-quick-btn';
    nextQuick.textContent = 'Next slide ›';
    nextQuick.addEventListener('click', () => {
      const nextIdx = (activeIdx + 1) % slides.length;
      this.activeCarouselSlideIndex = nextIdx;
      const frameDoc = this.adapter.getFrameDocument();
      const cid = carouselComp.getId?.();
      const carouselEl = (carouselComp.view?.el ??
        (cid ? frameDoc?.getElementById(cid) : null) ??
        frameDoc?.querySelector('.wk-carousel')) as HTMLElement | null;
      if (carouselEl) switchCarouselSlide(carouselEl, nextIdx);
      this.renderDynamicInspector('content');
    });
    navRow.append(prevQuick, nextQuick);
    box.append(navRow);

    host.append(box);
  }

  private renderContentControls(host: HTMLElement): void {
    const carouselComp = this.getSelectedCarousel();
    if (carouselComp) {
      this.renderCarouselControls(host, carouselComp);
      return;
    }

    let tooltipComp: Component | null = null;
    let currComp: Component | null = this.selected;
    while (currComp) {
      const cClasses = currComp.getClasses?.() || [];
      const cAttrs = (currComp.getAttributes?.() ?? {}) as Record<string, string>;
      if (
        cClasses.includes('wk-tooltip') ||
        /\bwk-tooltip\b/.test(cAttrs.class || '') ||
        currComp.get?.('name') === 'Tooltip' ||
        currComp.get?.('type') === 'tooltip'
      ) {
        tooltipComp = currComp;
        break;
      }
      currComp = currComp.parent?.() ?? null;
    }
    if (tooltipComp) {
      const compHtml = tooltipComp.toHTML?.() || tooltipComp.getInnerHTML?.() || '';
      const parser = new DOMParser();
      const doc = parser.parseFromString(compHtml, 'text/html');
      const tipSpan = doc.querySelector('[role="tooltip"]');
      const tipText = tipSpan?.textContent?.trim() || 'Helpful context';
      tipSpan?.remove();
      const trigText = doc.body.textContent?.trim() || 'Hover or focus me';

      const box = document.createElement('div');
      box.className = 'field-group tooltip-settings-group';

      const boxTitle = document.createElement('strong');
      boxTitle.className = 'field-group-title';
      boxTitle.textContent = 'Tooltip Settings';
      box.append(boxTitle);

      const trigLabel = document.createElement('label');
      trigLabel.className = 'field dynamic-field';
      trigLabel.textContent = 'Trigger text';
      const trigInput = document.createElement('input');
      trigInput.type = 'text';
      trigInput.value = trigText;
      trigLabel.append(trigInput);

      const msgLabel = document.createElement('label');
      msgLabel.className = 'field dynamic-field';
      msgLabel.textContent = 'Tooltip message';
      const msgInput = document.createElement('input');
      msgInput.type = 'text';
      msgInput.value = tipText;
      msgLabel.append(msgInput);

      const updateTooltip = () => {
        if (!tooltipComp) return;
        const newHtml = `${escapePageText(trigInput.value)}<span id="tooltip-copy" role="tooltip">${escapePageText(msgInput.value)}</span>`;
        tooltipComp.components(newHtml);
        const frameDoc = this.adapter.getFrameDocument();
        const cid = tooltipComp.getId?.();
        const el = (tooltipComp.view?.el ?? (cid ? frameDoc?.getElementById(cid) : null)) as HTMLElement | null;
        if (el) el.innerHTML = newHtml;
        this.syncInteractionRuntime();
      };

      trigInput.addEventListener('input', () => {
        updateTooltip();
        this.scheduleSave('Tooltip updated');
      });
      trigInput.addEventListener('change', () => {
        updateTooltip();
        this.saveNow('Tooltip updated');
      });

      msgInput.addEventListener('input', () => {
        updateTooltip();
        this.scheduleSave('Tooltip updated');
      });
      msgInput.addEventListener('change', () => {
        updateTooltip();
        this.saveNow('Tooltip updated');
      });

      box.append(trigLabel, msgLabel);
      host.append(box);
      return;
    }
    const tag = String(this.selected?.get('tagName') || '').toLowerCase();
    const textComponent = ['h1', 'h2', 'h3', 'h4', 'p', 'span', 'a', 'button'].includes(tag);
    if (textComponent) {
      const label = document.createElement('label');
      label.className = 'field dynamic-field';
      label.textContent = 'Text';
      const input = document.createElement('textarea');
      input.value = this.selected?.getInnerHTML?.() ?? '';
      input.addEventListener('input', () => this.selected?.components(input.value));
      label.append(input);
      host.append(label);
    }
    const attrs = this.selected?.getAttributes() as Record<string, string> | undefined;
    const classes = this.selected?.getClasses?.() || [];
    const isCounter = Boolean(
      classes.includes('wk-counter') ||
      attrs?.class?.includes('wk-counter') ||
      attrs?.['data-wk-value'] !== undefined ||
      this.selected?.get?.('name') === 'Animated counter' ||
      this.selected?.get?.('type') === 'counter',
    );
    if (isCounter) {
      const counterBox = document.createElement('div');
      counterBox.className = 'field-group counter-settings-group';

      const valLabel = document.createElement('label');
      valLabel.className = 'field dynamic-field';
      valLabel.textContent = 'Target number';
      const valInput = document.createElement('input');
      valInput.type = 'number';
      valInput.value = attrs?.['data-wk-value'] ?? '100';
      valInput.addEventListener('input', () => {
        if (!this.selected) return;
        this.adapter.updateComponentTraits(this.selected, { 'data-wk-value': valInput.value });
        this.selected.addAttributes({ 'data-wk-value': valInput.value });
        this.syncInteractionRuntime();
        this.scheduleSave('Counter updated');
      });
      valInput.addEventListener('change', () => {
        if (!this.selected) return;
        this.adapter.updateComponentTraits(this.selected, { 'data-wk-value': valInput.value });
        this.selected.addAttributes({ 'data-wk-value': valInput.value });
        this.syncInteractionRuntime();
        this.saveNow('Counter updated');
      });
      valLabel.append(valInput);

      const durLabel = document.createElement('label');
      durLabel.className = 'field dynamic-field';
      durLabel.textContent = 'Animation duration (ms)';
      const durInput = document.createElement('input');
      durInput.type = 'number';
      durInput.value = attrs?.['data-wk-duration'] ?? '1200';
      durInput.step = '100';
      durInput.min = '0';
      durInput.max = '10000';
      durInput.addEventListener('input', () => {
        if (!this.selected) return;
        this.adapter.updateComponentTraits(this.selected, { 'data-wk-duration': durInput.value });
        this.selected.addAttributes({ 'data-wk-duration': durInput.value });
        this.syncInteractionRuntime();
        this.scheduleSave('Counter updated');
      });
      durInput.addEventListener('change', () => {
        if (!this.selected) return;
        this.adapter.updateComponentTraits(this.selected, { 'data-wk-duration': durInput.value });
        this.selected.addAttributes({ 'data-wk-duration': durInput.value });
        this.syncInteractionRuntime();
        this.saveNow('Counter updated');
      });
      durLabel.append(durInput);

      counterBox.append(valLabel, durLabel);
      host.append(counterBox);
    }
    if (tag === 'a' || tag === 'button') {
      const label = document.createElement('label');
      label.className = 'field dynamic-field';
      label.textContent = 'Link';
      const input = document.createElement('input');
      input.value = attrs?.href ?? '';
      input.placeholder = 'https://example.com or #section';
      input.addEventListener(
        'input',
        () =>
          this.selected && this.adapter.updateComponentTraits(this.selected, { href: input.value }),
      );
      label.append(input);
      host.append(label);
    }
    if ((tag === 'img' || this.selected?.get('type') === 'image') && this.selected) {
      this.renderImageInspector(host, this.selected);
      return;
    }
    if (this.contentOnlyMode) {
      return;
    }
    this.renderStyleGroup(host, 'Typography', [
      'font-family',
      'font-size',
      'font-weight',
      'line-height',
      'letter-spacing',
      'text-align',
    ]);
    this.renderLayoutPresets(host);
    this.renderStyleGroup(host, 'Layout', [
      'width',
      'max-width',
      'min-height',
      'margin',
      'padding',
      'display',
      'gap',
      'justify-content',
      'align-items',
    ]);
    this.renderStyleGroup(host, 'Appearance', [
      'background-color',
      'border',
      'border-radius',
      'box-shadow',
      'opacity',
    ]);
  }

  private renderLayoutPresets(host: HTMLElement): void {
    if (!this.selected) return;
    const details = document.createElement('details');
    details.className = 'layout-presets';
    details.open = true;
    const summary = document.createElement('summary');
    summary.textContent = 'Layout preset';
    details.append(summary);
    const copy = document.createElement('p');
    copy.className = 'panel-note';
    copy.textContent = 'Apply a predictable layout foundation, then refine its controls below.';
    details.append(copy);
    const presetRow = document.createElement('div');
    presetRow.className = 'preset-grid';
    (['stack', 'flex', 'grid', 'container', 'free-position'] as LayoutPreset[]).forEach(
      (preset) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ghost-btn';
        button.dataset.layoutPreset = preset;
        button.textContent =
          preset === 'free-position' ? 'Free position' : preset[0].toUpperCase() + preset.slice(1);
        button.addEventListener('click', async () => {
          if (!this.selected) return;
          this.adapter.updateStyles(this.selected, getLayoutPresetStyles(preset));
          this.renderDynamicInspector('content');
          this.toast('Layout updated', `${button.textContent} preset applied.`);
        });
        presetRow.append(button);
      },
    );
    details.append(presetRow);
    host.append(details);
    this.renderStyleGroup(host, 'Layout controls', [...LAYOUT_CONTROLS]);
  }

  private renderStyleGroup(host: HTMLElement, title: string, properties: string[]): void {
    if (!this.selected) return;
    const details = document.createElement('details');
    details.open = title === 'Typography';
    const summary = document.createElement('summary');
    summary.textContent = title;
    details.append(summary);
    const styles = this.selected.getStyle() as Record<string, string>;
    properties.forEach((property) => {
      const label = document.createElement('label');
      label.className = 'field dynamic-field';
      label.textContent = property;
      const input = document.createElement('input');
      input.value = styles[property] ?? '';
      input.placeholder = 'Inherited';
      input.addEventListener(
        'input',
        () =>
          this.selected && this.adapter.updateStyles(this.selected, { [property]: input.value }),
      );
      const reset = document.createElement('button');
      reset.type = 'button';
      reset.className = 'mini-btn';
      reset.textContent = 'Reset';
      reset.addEventListener('click', async () => {
        if (!this.selected) return;
        this.selected.setStyle({ [property]: '' });
        input.value = '';
      });
      label.append(input, reset);
      details.append(label);
    });
    host.append(details);
  }

  private renderAdvancedControls(host: HTMLElement): void {
    const attrs = this.selected?.getAttributes() as Record<string, string> | undefined;
    [
      ['Element name', 'name'],
      ['HTML ID', 'id'],
      ['CSS classes', 'class'],
    ].forEach(([labelText, key]) => {
      const label = document.createElement('label');
      label.className = 'field dynamic-field';
      label.textContent = labelText;
      const input = document.createElement('input');
      input.value = attrs?.[key] ?? String(this.selected?.get('name') ?? '');
      input.addEventListener('input', () => {
        if (!this.selected) return;
        if (key === 'name') this.selected.set('name', input.value);
        else this.adapter.updateComponentTraits(this.selected, { [key]: input.value });
      });
      label.append(input);
      host.append(label);
    });
    const actions = document.createElement('div');
    actions.className = 'dynamic-actions';
    const duplicate = document.createElement('button');
    duplicate.textContent = 'Duplicate';
    duplicate.onclick = () => this.duplicateSelected();
    const remove = document.createElement('button');
    remove.textContent = 'Delete';
    remove.onclick = () => this.deleteSelected();
    const wrap = document.createElement('button');
    wrap.textContent = 'Wrap in container';
    wrap.onclick = () => this.wrapSelected();
    const detach = document.createElement('button');
    detach.textContent = 'Detach';
    detach.disabled = !this.isLayoutContainer(this.selected?.parent());
    detach.onclick = () => this.detachSelected();
    const group = document.createElement('button');
    group.textContent = 'Group selection';
    group.onclick = () => this.groupSelected();
    const ungroup = document.createElement('button');
    ungroup.textContent = 'Ungroup';
    ungroup.disabled = !this.isLayoutContainer(this.selected);
    ungroup.onclick = () => this.ungroupSelected();
    actions.append(duplicate, group, ungroup, wrap, detach, remove);
    host.append(actions);
    const lock = document.createElement('button');
    lock.className = 'mini-btn';
    lock.textContent = this.isLocked(this.selected) ? 'Unlock element' : 'Lock element';
    lock.onclick = () => {
      this.selected?.set('locked', !this.isLocked(this.selected));
      this.renderLayers();
      this.renderDynamicInspector('advanced');
    };
    host.append(lock);
  }

  private renderLayers(): void {
    const list = document.querySelector<HTMLElement>('#layerList');
    const root = this.adapter.getRoot();
    if (!list || !root) return;
    list.replaceChildren(...this.layerNodes(root.components().models));
  }

  private layerNodes(components: Component[]): HTMLElement[] {
    return components.flatMap((component) => {
      const row = document.createElement('div');
      row.className = `layer-row ${component === this.selected ? 'active' : ''} ${this.isLocked(component) ? 'locked' : ''}`;
      row.dataset.componentId = component.getId();
      row.tabIndex = 0;
      row.draggable = true;
      row.setAttribute('role', 'treeitem');
      const hasChildren = component.components().length > 0;
      row.innerHTML = `<button class="layer-toggle" data-layer-toggle aria-label="${hasChildren ? 'Expand or collapse' : 'No children'}">${hasChildren ? (this.expandedLayerIds.has(component.getId()) ? '▾' : '▸') : '·'}</button><span class="layer-icon">${this.isContainer(component) ? '▦' : '◦'}</span><strong>${componentLabel(component)}</strong><span class="layer-actions"><button data-layer-action="up" aria-label="Move up">↑</button><button data-layer-action="down" aria-label="Move down">↓</button><button data-layer-action="hide" aria-label="Hide or show">${component.get('visible') === false ? '◌' : '◉'}</button><button data-layer-action="lock" aria-label="Lock or unlock">${this.isLocked(component) ? '🔒' : '⌑'}</button><button data-layer-action="duplicate" aria-label="Duplicate">＋</button><button data-layer-action="delete" aria-label="Delete">×</button></span>`;
      row.querySelector('strong')?.addEventListener('dblclick', async () => {
        const next = await askText('Rename layer', componentLabel(component));
        if (next?.trim()) {
          component.set('name', next.trim());
          this.renderLayers();
          this.scheduleSave();
        }
      });
      const children = component.components().models;
      if (!children.length || !this.expandedLayerIds.has(component.getId())) return [row];
      const group = document.createElement('div');
      group.className = 'layer-children';
      group.append(...this.layerNodes(children));
      return [row, group];
    });
  }

  private toggleLayer(id: string): void {
    if (this.expandedLayerIds.has(id)) this.expandedLayerIds.delete(id);
    else this.expandedLayerIds.add(id);
    this.renderLayers();
  }

  private isLocked(component: Component | null): boolean {
    return Boolean(component?.get('locked'));
  }

  private isProtected(component: Component): boolean {
    return (
      component === this.adapter.getRoot() ||
      String(component.get('tagName')) === 'body' ||
      this.isLocked(component)
    );
  }

  private canDrop(dragged: Component, target: Component): boolean {
    return (
      dragged !== target &&
      !dragged.isChildOf(target) &&
      this.isContainer(target) &&
      !this.isProtected(dragged) &&
      !this.isLocked(target)
    );
  }

  private layerAction(action: string, component: Component): void {
    if (action === 'hide') component.set('visible', component.get('visible') === false);
    if (action === 'lock') component.set('locked', !this.isLocked(component));
    if (action === 'duplicate' && !this.isProtected(component)) {
      this.adapter.selectComponent(component);
      this.duplicateSelected();
    }
    if (action === 'delete' && !this.isProtected(component)) {
      this.adapter.selectComponent(component);
      this.deleteSelected();
    }
    if (action === 'up' || action === 'down') this.moveLayer(component, action === 'up' ? -1 : 1);
    this.renderLayers();
    this.scheduleSave();
  }

  private moveLayer(component: Component, delta: number): void {
    if (this.isProtected(component)) return;
    const parent = component.parent();
    if (!parent) return;
    const siblings = parent.components().models;
    const index = siblings.indexOf(component);
    const next = index + delta;
    if (next < 0 || next >= siblings.length) return;
    component.move(parent, { at: next });
  }

  private findById(
    id: string,
    components = this.adapter.getRoot()?.components().models ?? [],
  ): Component | null {
    for (const component of components) {
      if (component.getId() === id) return component;
      const found = this.findById(id, component.components().models);
      if (found) return found;
    }
    return null;
  }

  private updateColor(property: string, value: string, labelSelector: string): void {
    if (!this.selected) return;
    this.adapter.updateStyles(this.selected, { [property]: value });
    document
      .querySelector(labelSelector)
      ?.replaceChildren(document.createTextNode(value.toUpperCase()));
  }

  private duplicateSelected(): void {
    if (this.contentOnlyMode) return;
    const copy = firstComponent(this.adapter.duplicateComponent(this.selected ?? undefined));
    if (copy) this.adapter.selectComponent(copy);
  }

  private selectedComponents(): Component[] {
    const editor = this.adapter.getEditor() as unknown as {
      getSelectedAll?: () => Component[];
    };
    const selected = editor.getSelectedAll?.() ?? [];
    return selected.length ? selected : this.selected ? [this.selected] : [];
  }

  private groupSelected(): void {
    if (this.contentOnlyMode) return;
    const selected = this.selectedComponents();
    if (selected.length < 2) {
      this.toast(
        'Select two or more elements',
        'Use Ctrl/Cmd-click to add elements to the selection.',
      );
      return;
    }
    const parent = selected[0].parent();
    if (!parent || selected.some((component) => component.parent() !== parent)) {
      this.toast('Group unavailable', 'Selected elements must share the same parent container.');
      return;
    }
    const ordered = [...selected].sort(
      (left, right) =>
        parent.components().models.indexOf(left) - parent.components().models.indexOf(right),
    );
    const firstIndex = parent.components().models.indexOf(ordered[0]);
    const wrapper = parent.append({
      tagName: 'div',
      attributes: { class: 'wk-layout-container wk-selection-group' },
    })[0];
    if (!wrapper) return;
    wrapper.move(parent, { at: firstIndex });
    ordered.forEach((component) => component.move(wrapper, { at: wrapper.components().length }));
    this.adapter.selectComponent(wrapper);
    this.renderLayers();
    this.renderDynamicInspector('content');
    this.scheduleSave('Elements grouped');
    this.toast('Elements grouped', 'The selection is now a layout container.');
  }

  private ungroupSelected(): void {
    if (this.contentOnlyMode) return;
    const wrapper = this.selected;
    const parent = wrapper?.parent();
    if (!wrapper || !parent || !this.isLayoutContainer(wrapper)) return;
    const index = parent.components().models.indexOf(wrapper);
    const children = [...wrapper.components().models];
    children.forEach((child, offset) => child.move(parent, { at: index + offset }));
    wrapper.remove();
    if (children[0]) this.adapter.selectComponent(children[0]);
    this.renderLayers();
    this.renderDynamicInspector('content');
    this.scheduleSave('Elements ungrouped');
    this.toast('Elements ungrouped', 'Children remain in their original order.');
  }

  private deleteSelected(): void {
    if (this.contentOnlyMode) return;
    if (!this.selected || this.selected.get('tagName') === 'body') return;
    this.adapter.deleteComponent(this.selected);
    this.selected = null;
    this.renderLayers();
  }

  private isLayoutContainer(component: Component | null | undefined): boolean {
    const classes = String(
      (component?.getAttributes() as Record<string, unknown> | undefined)?.class ?? '',
    ).split(/\s+/);
    return classes.includes('wk-layout-container');
  }

  private wrapSelected(): void {
    if (this.contentOnlyMode) return;
    const selected = this.selected;
    const parent = selected?.parent();
    if (!selected || !parent || this.isProtected(selected) || parent === this.adapter.getRoot()) {
      return;
    }
    const wrapper = parent.append({
      tagName: 'div',
      attributes: { class: 'wk-layout-container' },
    })[0];
    if (!wrapper) return;
    selected.move(wrapper, { at: 0 });
    this.adapter.selectComponent(wrapper);
    this.renderLayers();
    this.renderDynamicInspector('content');
    this.scheduleSave('Element wrapped');
    this.toast('Container added', 'The selected element is now inside a layout container.');
  }

  private detachSelected(): void {
    if (this.contentOnlyMode) return;
    const selected = this.selected;
    const parent = selected?.parent();
    const grandparent = parent?.parent();
    if (!selected || !parent || !grandparent || !this.isLayoutContainer(parent)) return;
    const index = parent.components().models.indexOf(selected);
    selected.move(grandparent, { at: Math.max(0, index) });
    if (parent.components().length === 0) parent.remove();
    this.adapter.selectComponent(selected);
    this.renderLayers();
    this.renderDynamicInspector('content');
    this.scheduleSave('Element detached');
    this.toast(
      'Container removed',
      'The element remains on the canvas with its content preserved.',
    );
  }

  private setDevice(device: DeviceId): void {
    if (!['desktop', 'laptop', 'tablet', 'mobile'].includes(device)) return;
    this.device = device;
    this.adapter.setDevice(device);
    this.syncDeviceButtons(device);
    const frame = document.querySelector<HTMLElement>('.gjs-frame');
    if (frame) frame.style.maxWidth = `${deviceWidths[device]}px`;
    this.setCanvasStatus(device);
    requestAnimationFrame(() => this.fitCanvas());
  }

  private syncDeviceButtons(device: DeviceId): void {
    document
      .querySelectorAll<HTMLButtonElement>('.device')
      .forEach((button) =>
        button.classList.toggle('active', button.getAttribute('data-width') === device),
      );
  }

  private setCanvasStatus(device: DeviceId): void {
    void device;
    document
      .querySelector('#canvasSizeStatus')
      ?.replaceChildren(document.createTextNode(`Canvas ${this.adapter.getCanvasWidth()} px`));
  }

  private fitCanvas(): void {
    const area = document.querySelector('#canvasWrap');
    if (!area) return;
    const zoom = Math.max(
      10,
      Math.min(100, Math.floor(((area.clientWidth - 48) / this.adapter.getCanvasWidth()) * 100)),
    );
    document.querySelector('#zoomValue')?.replaceChildren(document.createTextNode(`${zoom}%`));
    this.adapter.setZoom(zoom);
  }

  private changeZoom(delta: number): void {
    const current = Number.parseInt(document.querySelector('#zoomValue')?.textContent ?? '90', 10);
    const next = Math.max(50, Math.min(120, current + delta));
    document.querySelector('#zoomValue')?.replaceChildren(document.createTextNode(`${next}%`));
    this.adapter.setZoom(next);
  }

  private togglePreview(): void {
    this.previewing = !this.previewing;
    document.querySelector('.editor-shell')?.classList.remove('tools-open', 'inspector-open');
    this.adapter.setPreview(this.previewing);
    document.querySelector('.editor-shell')?.classList.toggle('preview-mode', this.previewing);
    document.querySelector('.left-rail')?.classList.toggle('preview-hidden', this.previewing);
    document.querySelector('.inspector')?.classList.toggle('preview-hidden', this.previewing);
    const button = document.querySelector('#previewBtn');
    if (button) button.textContent = this.previewing ? 'Exit preview' : 'Preview';
    this.syncInteractionRuntime();
  }
  setPreviewMode(enabled: boolean): void {
    if (enabled !== this.previewing) this.togglePreview();
  }

  private switchPage(pageId: string): void {
    const current = this.project.pages.find((page) => page.id === this.project.currentPageId);
    if (current) {
      current.projectData = this.adapter.exportProjectData();
      current.updatedAt = new Date().toISOString();
    }
    let destination = this.project.pages.find((page) => page.id === pageId);
    if (!destination) {
      destination = {
        id: pageId,
        name: pageId,
        slug: uniqueSlug(this.project, pageId),
        projectData: null,
        updatedAt: new Date().toISOString(),
      };
      this.project.pages.push(destination);
    }
    this.project.currentPageId = destination.id;
    if (destination.projectData) this.adapter.loadProjectData(destination.projectData);
    else this.adapter.loadProjectData({ components: '<div class="site-canvas desktop"></div>' });
    document
      .querySelector('.crumb span')
      ?.replaceChildren(document.createTextNode(destination.name));
    this.renderPages();
    this.syncInteractionRuntime();
    const canEdit = this.canEditPage(destination.id);
    this.applyPageReadOnlyState(!canEdit);
    this.updateCommentTargetPill(this.selected);
    this.renderReviewComments();
    this.saveNow('Page switch');
  }

  private createPage(): void {
    const page = newPage(this.project, `New page ${this.project.pages.length + 1}`);
    this.project.pages.push(page);
    this.includePageInDefaultMenu(page);
    this.renderPages();
    this.switchPage(page.id);
  }

  private includePageInDefaultMenu(page: PageDocument): void {
    const menu = ensureMenus(this.project).find((item) => item.id === 'main');
    if (
      menu &&
      !menu.items.some((item) => item.pageId === page.id) &&
      menu.items.every((item) => item.id.startsWith('menu-') && item.type === 'page')
    )
      menu.items.push({ id: `menu-${page.id}`, label: page.name, type: 'page', pageId: page.id });
  }

  private openMenuLinkSettings(
    menu: ReturnType<typeof ensureMenus>[number],
    item?: ReturnType<typeof addMenuItem>,
  ): void {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    const parentChoices: Array<{ id: string; label: string }> = [];
    const flatten = (items: typeof menu.items, depth = 0) => {
      for (const link of items) {
        parentChoices.push({ id: link.id, label: `${'— '.repeat(depth)}${link.label}` });
        flatten(link.children ?? [], depth + 1);
      }
    };
    flatten(menu.items);
    const destination =
      item?.type === 'page'
        ? `page:${item.pageId}`
        : (item?.type ?? `page:${this.project.pages[0].id}`);
    backdrop.innerHTML = `<section class="setup-modal page-settings-modal" role="dialog" aria-modal="true" aria-labelledby="linkSettingsTitle"><div class="modal-head"><h2 id="linkSettingsTitle">Navigation link</h2><button type="button" class="mini-btn" data-close aria-label="Close navigation link">×</button></div><form><label class="field">Link label<input name="label" required value="${escapePageText(item?.label ?? '')}" /></label><label class="field">Destination<select name="destination">${this.project.pages.map((page) => `<option value="page:${escapePageText(page.id)}" ${destination === `page:${page.id}` ? 'selected' : ''}>${escapePageText(page.name)}</option>`).join('')}<option value="external" ${destination === 'external' ? 'selected' : ''}>Web address</option><option value="anchor" ${destination === 'anchor' ? 'selected' : ''}>Section on this page</option></select></label><label class="field" data-target-field>Address or section<input name="target" value="${escapePageText(item?.target ?? '')}" placeholder="https://example.com or #contact" /></label>${!item ? `<label class="field">Place under<select name="parent"><option value="">Main navigation</option>${parentChoices.map((parent) => `<option value="${escapePageText(parent.id)}">${escapePageText(parent.label)}</option>`).join('')}</select></label>` : ''}<p class="cloud-error" data-error role="alert" hidden></p><div class="modal-actions"><button type="button" class="ghost-btn" data-close>Cancel</button><button type="submit" class="primary-btn">Save navigation link</button></div></form></section>`;
    document.body.append(backdrop);
    const close = () => {
      releaseFocus();
      backdrop.remove();
    };
    const releaseFocus = bindDialog(backdrop, close);
    backdrop
      .querySelectorAll('[data-close]')
      .forEach((button) => button.addEventListener('click', close));
    const select = backdrop.querySelector<HTMLSelectElement>('[name="destination"]')!;
    const targetField = backdrop.querySelector<HTMLElement>('[data-target-field]')!;
    const update = () => {
      targetField.hidden = select.value.startsWith('page:');
    };
    select.addEventListener('change', update);
    update();
    backdrop.querySelector('form')!.addEventListener('submit', (event) => {
      event.preventDefault();
      const data = new FormData(event.target as HTMLFormElement);
      const label = String(data.get('label') ?? '').trim();
      const selected = select.value;
      const target = String(data.get('target') ?? '').trim();
      const type = selected.startsWith('page:')
        ? 'page'
        : selected === 'external'
          ? 'external'
          : 'anchor';
      if (
        !label ||
        (type === 'external' && !/^https?:\/\/\S+$/i.test(target)) ||
        (type === 'anchor' && !/^#[\w-]+$/.test(target))
      ) {
        const error = backdrop.querySelector<HTMLElement>('[data-error]')!;
        error.textContent = 'Add a label and a valid web address or section name.';
        error.hidden = false;
        return;
      }
      const pageId = type === 'page' ? selected.slice(5) : undefined;
      if (item) Object.assign(item, { label, type, target: type === 'page' ? '' : target, pageId });
      else
        addMenuItem(
          menu,
          label,
          type,
          type === 'page' ? '' : target,
          pageId,
          String(data.get('parent') ?? '') || undefined,
        );
      close();
      this.renderPages();
      this.saveNow('Navigation link updated');
    });
  }

  private openPageSettings(page: PageDocument): void {
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `<section class="setup-modal page-settings-modal" role="dialog" aria-modal="true" aria-labelledby="pageSettingsTitle"><div class="modal-head"><h2 id="pageSettingsTitle">Page settings</h2><button type="button" class="mini-btn" data-close aria-label="Close page settings">×</button></div><form><div class="setup-grid"><label class="field">Page name<input name="name" required maxlength="100" value="${escapePageText(page.name)}" /></label><label class="field">Page address<input name="slug" required value="${escapePageText(page.slug)}" ${page.isHomepage ? 'readonly' : ''} /></label><label class="field">Parent page<select name="parent"><option value="">No parent</option>${this.project.pages
      .filter((item) => item.id !== page.id)
      .map(
        (item) =>
          `<option value="${escapePageText(item.id)}" ${item.id === page.parentId ? 'selected' : ''}>${escapePageText(item.name)}</option>`,
      )
      .join(
        '',
      )}</select></label><label class="field">Folder<input name="folder" value="${escapePageText(page.folder ?? '')}" /></label><label class="field">SEO title<input name="title" value="${escapePageText(page.seo?.title ?? page.name)}" /></label><label class="field">Search visibility<select name="robots"><option value="index,follow">Visible in search</option><option value="noindex,nofollow" ${page.seo?.robots?.includes('noindex') ? 'selected' : ''}>Hidden from search</option></select></label></div><label class="field">SEO description<textarea name="description" rows="3">${escapePageText(page.seo?.description ?? '')}</textarea></label><p class="cloud-error" data-error role="alert" hidden></p><div class="modal-actions"><button type="button" class="ghost-btn" data-close>Cancel</button><button type="submit" class="primary-btn">Save page settings</button></div></form></section>`;
    document.body.append(backdrop);
    const close = () => {
      releaseFocus();
      backdrop.remove();
    };
    const releaseFocus = bindDialog(backdrop, close);
    backdrop
      .querySelectorAll('[data-close]')
      .forEach((button) => button.addEventListener('click', close));
    backdrop.querySelector('form')!.addEventListener('submit', (event) => {
      event.preventDefault();
      const data = new FormData(event.target as HTMLFormElement);
      const name = String(data.get('name') ?? '').trim();
      const slug = page.isHomepage
        ? '/'
        : '/' +
        String(data.get('slug') ?? '')
          .trim()
          .replace(/^\/+|\/+$/g, '')
          .toLowerCase();
      const error = backdrop.querySelector<HTMLElement>('[data-error]')!;
      if (
        !name ||
        !/^\/(?:[a-z0-9_-]+\/?)*$/.test(slug) ||
        this.project.pages.some((item) => item.id !== page.id && item.slug === slug)
      ) {
        error.textContent =
          'Enter a page name and a unique address using letters, numbers, and hyphens.';
        error.hidden = false;
        return;
      }
      if (!setPageParent(this.project, page.id, String(data.get('parent') ?? '') || null)) {
        error.textContent = 'Choose a parent that does not create a page hierarchy cycle.';
        error.hidden = false;
        return;
      }
      const previousName = page.name;
      page.name = name;
      page.slug = slug;
      setPageFolder(this.project, page.id, String(data.get('folder') ?? ''));
      page.seo = {
        ...page.seo,
        title: String(data.get('title') ?? '') || name,
        description: String(data.get('description') ?? ''),
        robots: String(data.get('robots') ?? 'index,follow'),
      };
      const updateLabels = (items: ReturnType<typeof ensureMenus>[number]['items']) => {
        for (const item of items) {
          if (item.pageId === page.id && item.label === previousName) item.label = name;
          updateLabels(item.children ?? []);
        }
      };
      ensureMenus(this.project).forEach((menu) => updateLabels(menu.items));
      if (page.id === this.project.currentPageId) {
        const crumb = document.querySelector('.crumb span');
        if (crumb) crumb.textContent = name;
      }
      close();
      this.renderPages();
      this.saveNow('Page settings updated');
    });
  }

  private async openPagePermissionsModal(page: PageDocument): Promise<void> {
    const cloud =
      this.cloudClient ??
      (window as unknown as { WebKiln?: { cloud?: WebKilnApiClient } }).WebKiln?.cloud;
    const workspaceId = this.workspaceId || this.project.site?.workspaceId || '';
    const siteId = this.project.site?.id || '';

    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';
    backdrop.innerHTML = `
      <section class="setup-modal page-permissions-modal" role="dialog" aria-modal="true" aria-labelledby="pagePermTitle">
        <div class="modal-head">
          <div>
            <p class="eyebrow">Governance & Security</p>
            <h2 id="pagePermTitle">Page permissions · ${escapePageText(page.name)}</h2>
          </div>
          <button type="button" class="mini-btn" data-close aria-label="Close dialog">×</button>
        </div>
        <p class="panel-note" style="margin: 8px 0 16px;">
          Configure specific access rules for this page. Members with a custom rule will be restricted to their assigned page role. Other members will follow their workspace role.
        </p>
        <div class="permissions-list-container" data-perm-list style="max-height: 320px; overflow-y: auto;">
          <div style="padding: 16px; text-align: center; color: #8f999f;">Loading workspace members…</div>
        </div>
        <p class="cloud-error" data-error role="alert" hidden></p>
        <div class="modal-actions" style="margin-top: 16px;">
          <button type="button" class="ghost-btn" data-close>Cancel</button>
          <button type="button" class="primary-btn" data-save-perms>Save permissions</button>
        </div>
      </section>
    `;

    document.body.append(backdrop);
    const close = () => {
      release();
      backdrop.remove();
    };
    const release = bindDialog(backdrop, close);
    backdrop.querySelectorAll('[data-close]').forEach((btn) => btn.addEventListener('click', close));

    const listContainer = backdrop.querySelector<HTMLElement>('[data-perm-list]')!;
    const errorEl = backdrop.querySelector<HTMLElement>('[data-error]')!;
    const saveBtn = backdrop.querySelector<HTMLButtonElement>('[data-save-perms]')!;

    let members: Array<{ userId: string; user?: { name?: string; email?: string }; role: string }> = [];
    try {
      if (cloud && workspaceId) {
        const result = await cloud.listMembers(workspaceId);
        members = result.members.map((m) => ({
          userId: m.userId,
          user: m.user,
          role: m.role,
        }));
      }
    } catch (_err) {
      void _err;
    }

    if (!members.length) {
      listContainer.innerHTML = '<div style="padding: 16px; text-align: center; color: #8f999f;">No additional members found in this workspace.</div>';
    } else {
      const pagePerms = this.pagePermissions.filter((p) => p.pageId === page.id);
      listContainer.innerHTML = members
        .map((member) => {
          const currentRule = pagePerms.find((p) => p.userId === member.userId);
          const assignedRole = currentRule?.role ?? 'default';
          const userName = member.user?.name || member.user?.email || member.userId.slice(0, 8);
          const userEmail = member.user?.email || '';

          return `
            <div class="perm-member-row" data-user-id="${escapePageText(member.userId)}" style="display:flex;align-items:center;justify-content:space-between;padding:10px 0;border-bottom:1px solid var(--line,#293034);gap:12px;">
              <div style="flex:1;min-width:0;">
                <strong style="display:block;font-size:13px;color:#f4f5f3;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapePageText(userName)}</strong>
                <small style="color:#8f999f;font-size:11px;">${escapePageText(userEmail)} · Workspace: ${escapePageText(member.role)}</small>
              </div>
              <select class="perm-role-select" style="background:#0d1012;color:#f4f5f3;border:1px solid var(--line,#293034);border-radius:6px;padding:6px 10px;font-size:12px;">
                <option value="default" ${assignedRole === 'default' ? 'selected' : ''}>Workspace default (${escapePageText(member.role)})</option>
                <option value="designer" ${assignedRole === 'designer' ? 'selected' : ''}>Designer (Edit layout & content)</option>
                <option value="content_editor" ${assignedRole === 'content_editor' ? 'selected' : ''}>Content Editor (Edit text & images)</option>
                <option value="viewer" ${assignedRole === 'viewer' ? 'selected' : ''}>Read-only (View only)</option>
              </select>
            </div>
          `;
        })
        .join('');
    }

    saveBtn.addEventListener('click', async () => {
      if (!cloud || !siteId) {
        close();
        return;
      }
      saveBtn.disabled = true;
      saveBtn.textContent = 'Saving…';
      errorEl.hidden = true;

      try {
        const rows = listContainer.querySelectorAll<HTMLElement>('.perm-member-row');
        for (const row of Array.from(rows)) {
          const userId = row.dataset.userId;
          const select = row.querySelector<HTMLSelectElement>('.perm-role-select');
          if (!userId || !select) continue;
          const val = select.value;
          const existing = this.pagePermissions.find((p) => p.pageId === page.id && p.userId === userId);

          if (val === 'default') {
            if (existing) {
              await cloud.deletePagePermission(siteId, page.id, userId).catch(() => undefined);
              this.pagePermissions = this.pagePermissions.filter((p) => !(p.pageId === page.id && p.userId === userId));
            }
          } else {
            if (!existing || existing.role !== val) {
              await cloud.setPagePermission(siteId, page.id, userId, val);
              if (existing) {
                existing.role = val;
              } else {
                this.pagePermissions.push({
                  id: crypto.randomUUID(),
                  workspaceId,
                  siteId,
                  pageId: page.id,
                  userId,
                  role: val,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                });
              }
            }
          }
        }

        this.renderPages();
        const canEdit = this.canEditPage(this.project.currentPageId);
        this.applyPageReadOnlyState(!canEdit);
        this.toast('Permissions updated', `Page permissions for "${page.name}" were saved.`);
        close();
      } catch (err) {
        saveBtn.disabled = false;
        saveBtn.textContent = 'Save permissions';
        errorEl.textContent = err instanceof Error ? err.message : 'Could not save permissions.';
        errorEl.hidden = false;
      }
    });
  }

  private bindReviewComments(): void {
    const commentsBtn = document.querySelector<HTMLButtonElement>('#commentsBtn');
    commentsBtn?.addEventListener('click', () => {
      this.openPanel('comments');
      const shell = document.querySelector<HTMLElement>('.editor-shell');
      if (shell && !shell.classList.contains('tools-open')) {
        shell.classList.add('tools-open');
      }
    });

    const railCommentsTab = document.querySelector<HTMLButtonElement>('.rail-tab[data-panel="comments"]');
    railCommentsTab?.addEventListener('click', () => {
      this.renderReviewComments();
    });

    const closeBtn = document.querySelector('#commentsPanel .close-panel');
    closeBtn?.addEventListener('click', () => {
      this.openPanel('blocks');
    });

    document.querySelectorAll<HTMLButtonElement>('[data-comment-filter]').forEach((chip) => {
      chip.addEventListener('click', () => {
        document.querySelectorAll('[data-comment-filter]').forEach((btn) => btn.classList.remove('active'));
        chip.classList.add('active');
        this.activeCommentFilter = (chip.dataset.commentFilter as 'all' | 'open' | 'resolved') || 'all';
        this.renderReviewComments();
      });
    });

    const mentionSelect = document.querySelector<HTMLSelectElement>('#commentMentionSelect');
    mentionSelect?.addEventListener('change', () => {
      const selectedUserId = mentionSelect.value;
      if (!selectedUserId) return;
      const member = this.workspaceMembers.find((m) => m.userId === selectedUserId);
      const memberLabel =
        member?.user?.name ||
        member?.user?.email?.split('@')[0] ||
        selectedUserId.slice(0, 8);
      const textarea = document.querySelector<HTMLTextAreaElement>('#newCommentInput');
      if (textarea) {
        const start = textarea.selectionStart ?? textarea.value.length;
        const end = textarea.selectionEnd ?? textarea.value.length;
        const text = textarea.value;
        const before = text.substring(0, start);
        const after = text.substring(end);
        const prefix = before.length > 0 && !before.endsWith(' ') ? ' ' : '';
        const insertion = `${prefix}@${memberLabel} `;
        textarea.value = before + insertion + after;
        const nextPos = start + insertion.length;
        textarea.focus();
        textarea.setSelectionRange(nextPos, nextPos);
      }
      mentionSelect.value = '';
    });

    const postBtn = document.querySelector<HTMLButtonElement>('#postCommentBtn');
    postBtn?.addEventListener('click', () => {
      void this.submitNewComment();
    });

    const textarea = document.querySelector<HTMLTextAreaElement>('#newCommentInput');
    textarea?.addEventListener('input', () => {
      this.handleCommentInputAutocomplete();
    });

    textarea?.addEventListener('keydown', (e) => {
      const popup = document.querySelector<HTMLElement>('#commentAutocompletePopup');
      const isPopupOpen = popup && !popup.hidden && this.activeAutocompleteItems.length > 0;

      if (isPopupOpen) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.activeAutocompleteIndex = (this.activeAutocompleteIndex + 1) % this.activeAutocompleteItems.length;
          this.renderCommentAutocomplete();
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.activeAutocompleteIndex =
            (this.activeAutocompleteIndex - 1 + this.activeAutocompleteItems.length) %
            this.activeAutocompleteItems.length;
          this.renderCommentAutocomplete();
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          this.selectCommentAutocompleteItem(this.activeAutocompleteIndex);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          this.closeCommentAutocomplete();
          return;
        }
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        this.closeCommentAutocomplete();
        void this.submitNewComment();
      }
    });

    textarea?.addEventListener('blur', () => {
      setTimeout(() => this.closeCommentAutocomplete(), 160);
    });

    this.updateCommentTargetPill(this.selected);
    void this.loadWorkspaceMembersForMentions();
    void this.loadReviewComments();
  }

  private handleCommentInputAutocomplete(): void {
    const textarea = document.querySelector<HTMLTextAreaElement>('#newCommentInput');
    const popup = document.querySelector<HTMLElement>('#commentAutocompletePopup');
    if (!textarea || !popup) return;

    const cursorPos = textarea.selectionStart ?? textarea.value.length;
    const textBeforeCursor = textarea.value.slice(0, cursorPos);

    const match = textBeforeCursor.match(/(?:^|\s)([@#])([a-zA-Z0-9._ -]*)$/);
    if (!match) {
      this.closeCommentAutocomplete();
      return;
    }

    const triggerChar = match[1] as '@' | '#';
    const query = match[2].trim().toLowerCase();
    const triggerIndex = textBeforeCursor.lastIndexOf(triggerChar);

    this.activeAutocompleteTrigger = {
      trigger: triggerChar,
      startIndex: triggerIndex,
      query,
    };

    if (triggerChar === '@') {
      const filtered = this.workspaceMembers.filter((m) => {
        const name = (m.user?.name || '').toLowerCase();
        const email = (m.user?.email || '').toLowerCase();
        const id = m.userId.toLowerCase();
        return !query || name.includes(query) || email.includes(query) || id.includes(query);
      });

      this.activeAutocompleteItems = filtered.map((m) => {
        const name = m.user?.name || m.user?.email?.split('@')[0] || m.userId;
        return {
          type: 'member',
          id: m.userId,
          label: name,
          sub: m.role || 'Member',
        };
      });
    } else {
      const pages = this.project.pages || [];
      const filtered = pages.filter((p) => {
        const name = p.name.toLowerCase();
        const slug = (p.slug || '').toLowerCase();
        return !query || name.includes(query) || slug.includes(query);
      });

      this.activeAutocompleteItems = filtered.map((p) => ({
        type: 'page',
        id: p.id,
        label: p.name,
        sub: p.slug ? `/${p.slug.replace(/^\//, '')}` : 'Page',
      }));
    }

    if (!this.activeAutocompleteItems.length) {
      this.closeCommentAutocomplete();
      return;
    }

    this.activeAutocompleteIndex = 0;
    this.renderCommentAutocomplete();
  }

  private renderCommentAutocomplete(): void {
    const popup = document.querySelector<HTMLElement>('#commentAutocompletePopup');
    if (!popup) return;

    popup.hidden = false;
    popup.innerHTML = this.activeAutocompleteItems
      .map((item, idx) => {
        const isSelected = idx === this.activeAutocompleteIndex;
        const icon =
          item.type === 'page'
            ? `<div class="autocomplete-item-icon page-icon" aria-hidden="true"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></div>`
            : `<div class="autocomplete-item-icon" aria-hidden="true">${escapePageText(item.label.slice(0, 2).toUpperCase())}</div>`;
        return `
          <div class="autocomplete-item ${isSelected ? 'active' : ''}" data-autocomplete-idx="${idx}" role="option" aria-selected="${isSelected}">
            ${icon}
            <div class="autocomplete-item-meta">
              <span class="autocomplete-item-title">${escapePageText(item.label)}</span>
              <span class="autocomplete-item-subtitle">${escapePageText(item.sub || '')}</span>
            </div>
          </div>
        `;
      })
      .join('');

    popup.querySelectorAll<HTMLElement>('.autocomplete-item').forEach((el) => {
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        const idx = Number(el.dataset.autocompleteIdx);
        this.selectCommentAutocompleteItem(idx);
      });
    });
  }

  private selectCommentAutocompleteItem(idx: number): void {
    const item = this.activeAutocompleteItems[idx];
    const trigger = this.activeAutocompleteTrigger;
    const textarea = document.querySelector<HTMLTextAreaElement>('#newCommentInput');
    if (!item || !trigger || !textarea) {
      this.closeCommentAutocomplete();
      return;
    }

    const value = textarea.value;
    const beforeTrigger = value.substring(0, trigger.startIndex);
    const afterCursor = value.substring(textarea.selectionStart ?? value.length);

    const prefix = trigger.trigger;
    const inserted = `${prefix}${item.label} `;
    textarea.value = beforeTrigger + inserted + afterCursor;

    const newCursor = beforeTrigger.length + inserted.length;
    textarea.focus();
    textarea.setSelectionRange(newCursor, newCursor);

    this.closeCommentAutocomplete();
  }

  private closeCommentAutocomplete(): void {
    const popup = document.querySelector<HTMLElement>('#commentAutocompletePopup');
    if (popup) {
      popup.hidden = true;
      popup.innerHTML = '';
    }
    this.activeAutocompleteTrigger = null;
    this.activeAutocompleteItems = [];
    this.activeAutocompleteIndex = 0;
  }

  private updateCommentTargetPill(component: Component | null): void {
    const pill = document.querySelector<HTMLElement>('#commentTargetPill');
    const label = document.querySelector<HTMLElement>('#commentTargetLabel');
    if (!pill || !label) return;

    if (component) {
      const compId = this.adapter.getComponentId(component);
      const compName = componentLabel(component);
      this.selectedComponentForComment = compId ? { id: compId, name: compName } : null;
      label.textContent = `Target: ${compName || compId}`;
      pill.style.borderColor = 'var(--gold, #f3b562)';
      pill.style.color = '#f4f5f3';
    } else {
      this.selectedComponentForComment = null;
      const currentPage = this.project.pages.find((p) => p.id === this.project.currentPageId);
      label.textContent = `Scope: Page (${currentPage?.name ?? 'Home'})`;
      pill.style.borderColor = '#2d363c';
      pill.style.color = '#9faab2';
    }
  }

  private async loadWorkspaceMembersForMentions(): Promise<void> {
    const cloud =
      this.cloudClient ??
      (window as unknown as { WebKiln?: { cloud?: WebKilnApiClient } }).WebKiln?.cloud;
    const workspaceId = this.workspaceId || this.project.site?.workspaceId;
    if (!cloud || !workspaceId) return;

    try {
      const result = await cloud.listMembers(workspaceId);
      this.workspaceMembers = result.members || [];
      const select = document.querySelector<HTMLSelectElement>('#commentMentionSelect');
      if (!select) return;
      select.innerHTML = '<option value="">@ Mention member…</option>';
      this.workspaceMembers.forEach((m) => {
        const name = m.user?.name || m.user?.email || m.userId;
        const opt = document.createElement('option');
        opt.value = m.userId;
        opt.textContent = `@${name} (${m.role})`;
        select.appendChild(opt);
      });
    } catch (_err) {
      void _err;
    }
  }

  private async loadReviewComments(): Promise<void> {
    const cloud =
      this.cloudClient ??
      (window as unknown as { WebKiln?: { cloud?: WebKilnApiClient } }).WebKiln?.cloud;
    const siteId = this.project.site?.id;
    if (!cloud || !siteId) {
      this.updateCommentsCountBadges();
      this.renderReviewComments();
      return;
    }

    try {
      this.reviewComments = await cloud.listReviewComments(siteId);
    } catch (_err) {
      void _err;
    }
    this.updateCommentsCountBadges();
    this.renderReviewComments();
  }

  private updateCommentsCountBadges(): void {
    const openCount = this.reviewComments.filter((c) => c.status === 'open').length;
    const resolvedCount = this.reviewComments.filter((c) => c.status === 'resolved').length;
    const allCount = this.reviewComments.length;

    const countAllEl = document.querySelector('#countAllComments');
    if (countAllEl) countAllEl.textContent = String(allCount);
    const countOpenEl = document.querySelector('#countOpenComments');
    if (countOpenEl) countOpenEl.textContent = String(openCount);
    const countResolvedEl = document.querySelector('#countResolvedComments');
    if (countResolvedEl) countResolvedEl.textContent = String(resolvedCount);

    const commentsBtnBadge = document.querySelector<HTMLElement>('#commentsCountBadge');
    if (commentsBtnBadge) {
      commentsBtnBadge.textContent = String(openCount);
      commentsBtnBadge.hidden = openCount === 0;
    }
    const inspectorBadge = document.querySelector<HTMLElement>('#inspectorCommentsBadge');
    if (inspectorBadge) {
      inspectorBadge.textContent = String(openCount);
      inspectorBadge.hidden = openCount === 0;
    }
  }

  private async submitNewComment(): Promise<void> {
    const cloud =
      this.cloudClient ??
      (window as unknown as { WebKiln?: { cloud?: WebKilnApiClient } }).WebKiln?.cloud;
    const siteId = this.project.site?.id;
    const textarea = document.querySelector<HTMLTextAreaElement>('#newCommentInput');
    const postBtn = document.querySelector<HTMLButtonElement>('#postCommentBtn');
    const mentionSelect = document.querySelector<HTMLSelectElement>('#commentMentionSelect');
    if (!textarea) return;

    const body = textarea.value.trim();
    if (!body) return;

    const mentions: Array<{ userId: string; label?: string }> = [];
    const candidateMembers = [...this.workspaceMembers].sort(
      (a, b) => (b.user?.name?.length || 0) - (a.user?.name?.length || 0),
    );

    for (const member of candidateMembers) {
      const name = member.user?.name?.trim();
      if (!name) continue;
      const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`@${escapedName}\\b`, 'i');
      if (regex.test(body)) {
        if (!mentions.some((item) => item.userId === member.userId)) {
          mentions.push({
            userId: member.userId,
            label: name,
          });
        }
      }
    }

    const singleMatches = body.match(/@([a-zA-Z0-9._-]+)/g);
    if (singleMatches) {
      for (const match of singleMatches) {
        const handle = match.substring(1).toLowerCase();
        const member = this.workspaceMembers.find((m) => {
          const mName = (m.user?.name || '').toLowerCase();
          const mEmail = (m.user?.email || '').toLowerCase();
          return mName.includes(handle) || mEmail.startsWith(handle) || m.userId.toLowerCase() === handle;
        });
        if (member) {
          if (!mentions.some((item) => item.userId === member.userId)) {
            mentions.push({
              userId: member.userId,
              label: member.user?.name || member.user?.email || member.userId,
            });
          }
        } else {
          if (!mentions.some((item) => item.label?.toLowerCase() === handle)) {
            mentions.push({
              userId: 'mention',
              label: match.substring(1),
            });
          }
        }
      }
    }

    if (!cloud || !siteId) {
      this.reviewComments.unshift({
        id: crypto.randomUUID(),
        workspaceId: this.workspaceId || 'local',
        siteId: siteId || 'local',
        pageId: this.project.currentPageId,
        componentId: this.selectedComponentForComment?.id ?? null,
        body,
        status: 'open',
        mentions,
        createdBy: this.currentUserId || 'local-user',
        createdAt: new Date().toISOString(),
      });
      textarea.value = '';
      this.updateCommentsCountBadges();
      this.renderReviewComments();
      return;
    }

    postBtn?.setAttribute('disabled', 'true');
    try {
      const componentId = this.selectedComponentForComment?.id;
      const pageId = this.project.currentPageId;

      await cloud.createReviewComment(siteId, {
        body,
        pageId: pageId || undefined,
        componentId: componentId || undefined,
        mentions: mentions.length ? mentions : undefined,
      });

      textarea.value = '';
      if (mentionSelect) mentionSelect.value = '';
      await this.loadReviewComments();
    } catch (_err) {
      void _err;
    } finally {
      postBtn?.removeAttribute('disabled');
    }
  }

  private renderReviewComments(): void {
    const stream = document.querySelector<HTMLElement>('#commentsStream');
    if (!stream) return;

    let filtered = [...this.reviewComments];
    if (this.activeCommentFilter === 'open') {
      filtered = filtered.filter((c) => c.status === 'open');
    } else if (this.activeCommentFilter === 'resolved') {
      filtered = filtered.filter((c) => c.status === 'resolved');
    } else {
      filtered.sort((a, b) => {
        if (a.status === 'open' && b.status === 'resolved') return -1;
        if (a.status === 'resolved' && b.status === 'open') return 1;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
    }

    if (!filtered.length) {
      const emptyMsg =
        this.activeCommentFilter === 'open'
          ? 'No open review comments.'
          : this.activeCommentFilter === 'resolved'
            ? 'No resolved review comments.'
            : 'No comments yet. Leave a review note above.';
      stream.innerHTML = `<div class="empty-comments-state">${escapePageText(emptyMsg)}</div>`;
      return;
    }

    stream.innerHTML = filtered
      .map((comment) => {
        const isResolved = comment.status === 'resolved';
        const authorName = comment.author?.name || comment.author?.email || comment.createdBy.slice(0, 8);
        const initials = (authorName || 'U')
          .split(/[\s@._]+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((s) => s[0].toUpperCase())
          .join('') || 'U';
        const formattedDate = new Date(comment.createdAt).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });

        const targetBadge = comment.componentId
          ? `<span class="comment-target-badge"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg>Component: ${escapePageText(comment.componentId.slice(0, 14))}</span>`
          : `<span class="comment-target-badge"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>Page level</span>`;

        const mentionsBar =
          comment.mentions && comment.mentions.length > 0
            ? `
              <div class="comment-mentions-bar">
                <span class="comment-mentions-label">Mentioned</span>
                ${comment.mentions
              .map(
                (m) => `
                  <span class="comment-mention-pill">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    @${escapePageText(m.label || m.userId)}
                  </span>
                `,
              )
              .join('')}
              </div>
            `
            : '';

        const candidateNames = Array.from(
          new Set([
            ...(comment.mentions?.map((m) => m.label?.trim()).filter(Boolean) as string[]),
            ...this.workspaceMembers.map((m) => m.user?.name?.trim()).filter(Boolean) as string[],
          ]),
        )
          .filter(Boolean)
          .sort((a, b) => b.length - a.length);

        const patterns = [
          ...candidateNames.map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
          '[a-zA-Z0-9._-]+',
        ];

        const mentionRegex = new RegExp(`@(${patterns.join('|')})`, 'gi');

        let formattedBody = escapePageText(comment.body);
        formattedBody = formattedBody.replace(
          mentionRegex,
          '<mark class="comment-mention-tag"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>@$1</mark>',
        );

        const candidatePages = [...(this.project.pages || [])].sort(
          (a, b) => b.name.length - a.name.length,
        );
        const pagePatterns = [
          ...candidatePages.map((p) => p.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
          '[a-zA-Z0-9_-]+',
        ];
        const pageRegex = new RegExp(`#(${pagePatterns.join('|')})`, 'gi');

        formattedBody = formattedBody.replace(pageRegex, (_match, pageName: string) => {
          const foundPage = candidatePages.find(
            (p) => p.name.toLowerCase() === pageName.toLowerCase(),
          );
          const pageId = foundPage ? foundPage.id : '';
          return `<mark class="comment-page-tag" data-comment-page-target="${escapePageText(pageId)}" title="Navigate to ${escapePageText(pageName)}"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>#${escapePageText(pageName)}</mark>`;
        });

        const viewTargetBtn = comment.componentId
          ? `<button class="comment-action-btn view-target" data-comment-target="${escapePageText(comment.componentId)}" data-comment-page="${escapePageText(comment.pageId ?? '')}" type="button">Inspect</button>`
          : '';

        const statusToggleBtn = isResolved
          ? `<button class="comment-action-btn reopen" data-comment-toggle="${escapePageText(comment.id)}" data-next-status="open" type="button">Reopen</button>`
          : `<button class="comment-action-btn resolve" data-comment-toggle="${escapePageText(comment.id)}" data-next-status="resolved" type="button">Resolve</button>`;

        return `
          <article class="comment-card ${isResolved ? 'resolved' : ''}" data-comment-id="${escapePageText(comment.id)}">
            <div class="comment-card-header">
              <div class="comment-author-badge">
                <div class="comment-avatar" aria-hidden="true">${escapePageText(initials)}</div>
                <div class="comment-author-meta">
                  <span class="comment-author-name">${escapePageText(authorName)}</span>
                  <time class="comment-date">${escapePageText(formattedDate)}</time>
                </div>
              </div>
              <span class="comment-status-pill ${isResolved ? 'resolved' : 'open'}">${isResolved ? 'Resolved' : 'Open'}</span>
            </div>
            ${targetBadge}
            ${mentionsBar}
            <p class="comment-body-text">${formattedBody}</p>
            <div class="comment-card-actions">
              ${viewTargetBtn}
              ${statusToggleBtn}
            </div>
          </article>
        `;
      })
      .join('');

    stream.querySelectorAll<HTMLButtonElement>('[data-comment-toggle]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const commentId = btn.dataset.commentToggle;
        const nextStatus = btn.dataset.nextStatus as 'open' | 'resolved';
        if (!commentId || !nextStatus) return;
        const cloud =
          this.cloudClient ??
          (window as unknown as { WebKiln?: { cloud?: WebKilnApiClient } }).WebKiln?.cloud;
        if (!cloud) {
          const c = this.reviewComments.find((item) => item.id === commentId);
          if (c) c.status = nextStatus;
          this.updateCommentsCountBadges();
          this.renderReviewComments();
          return;
        }
        btn.disabled = true;
        try {
          await cloud.updateReviewCommentStatus(commentId, nextStatus);
          await this.loadReviewComments();
        } catch (_err) {
          void _err;
          btn.disabled = false;
        }
      });
    });

    stream.querySelectorAll<HTMLButtonElement>('[data-comment-target]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const targetId = btn.dataset.commentTarget;
        const pageId = btn.dataset.commentPage;
        if (!targetId) return;
        this.highlightComponentTarget(targetId, pageId);
      });
    });

    stream.querySelectorAll<HTMLElement>('[data-comment-page-target]').forEach((tag) => {
      tag.addEventListener('click', () => {
        const pageId = tag.dataset.commentPageTarget;
        if (pageId && pageId !== this.project.currentPageId) {
          this.switchPage(pageId);
        }
      });
    });
  }

  private highlightComponentTarget(targetId: string, pageId?: string): void {
    if (pageId && pageId !== this.project.currentPageId) {
      this.switchPage(pageId);
    }
    const editor = (this.adapter as unknown as {
      requireEditor?: () => {
        getWrapper?: () => { find?: (selector: string) => Component[] };
        select?: (c: Component) => void;
      };
    }).requireEditor?.();
    if (!editor) return;
    const wrapper = editor.getWrapper?.();
    const found =
      wrapper?.find?.('#' + targetId)?.[0] ??
      wrapper?.find?.('[data-gjs-type]')?.find((c: Component) => c.getId?.() === targetId);
    if (found) {
      editor.select?.(found);
      const el = found.getView?.()?.el;
      if (el) {
        el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        el.classList.add('comment-target-flash');
        setTimeout(() => el.classList.remove('comment-target-flash'), 2000);
      }
    }
  }

  private renderPages(): void {
    const panel = document.querySelector('#pagesPanel');
    const tree = document.querySelector('#siteTree');
    if (!panel || !tree) return;
    this.renderMenus(panel, tree);
    panel
      .querySelectorAll('.page-folder, .page-row, .deleted-page-row')
      .forEach((row) => row.remove());
    const folders = new Map<string, HTMLElement>();
    this.project.pages.forEach((page) => {
      const canEdit = this.canEditPage(page.id);
      const isOwnerOrAdmin = ['owner', 'administrator'].includes(canonicalRole(this.role));
      const lockIndicator = !canEdit
        ? '<span class="page-lock-pill" title="Read-only page"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span>'
        : '';
      const permissionsBtn = isOwnerOrAdmin
        ? `<button type="button" data-page-action="permissions" data-page-id="${escapePageText(page.id)}" aria-label="Page permissions" title="Page permissions"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg></button>`
        : '';

      const row = document.createElement('div');
      row.className = `page-row ${page.id === this.project.currentPageId ? 'active' : ''}`;
      row.dataset.pageId = page.id;
      row.draggable = true;
      row.innerHTML = `<button data-page-action="open" data-page-id="${escapePageText(page.id)}" class="page-open"><span>${page.parentId ? '↳' : '▧'}</span><strong>${page.folder ? `${escapePageText(page.folder)} / ` : ''}${escapePageText(page.name)}</strong>${lockIndicator}<small>${page.isHomepage ? 'Home · ' : ''}${escapePageText(page.slug)}${page.settings?.showInNavigation === false ? ' · Hidden' : ''}${page.settings?.passwordProtected ? ' · Protected' : ''}</small></button><details class="page-actions"><summary aria-label="Page actions for ${escapePageText(page.name)}">•••</summary><div class="page-action-menu"><button data-page-action="home" data-page-id="${escapePageText(page.id)}" aria-label="Set homepage">⌂</button><button data-page-action="rename" data-page-id="${escapePageText(page.id)}" aria-label="Rename page">Aa</button><button data-page-action="parent" data-page-id="${escapePageText(page.id)}" aria-label="Set parent page">↳</button><button data-page-action="folder" data-page-id="${escapePageText(page.id)}" aria-label="Set page folder">▤</button><button data-page-action="seo" data-page-id="${escapePageText(page.id)}" aria-label="Edit SEO">SEO</button><button data-page-action="navigation" data-page-id="${escapePageText(page.id)}" aria-label="Toggle navigation visibility">☰</button><button data-page-action="protect" data-page-id="${escapePageText(page.id)}" aria-label="Toggle page protection"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></button>${permissionsBtn}<button data-page-action="up" data-page-id="${escapePageText(page.id)}" aria-label="Move page up">↑</button><button data-page-action="down" data-page-id="${escapePageText(page.id)}" aria-label="Move page down">↓</button><button data-page-action="duplicate" data-page-id="${escapePageText(page.id)}" aria-label="Duplicate page">＋</button><button data-page-action="delete" data-page-id="${escapePageText(page.id)}" aria-label="Archive page">×</button></div></details>`;
      if (page.folder) {
        let folder = folders.get(page.folder);
        if (!folder) {
          folder = document.createElement('details');
          folder.className = 'page-folder';
          folder.setAttribute('open', '');
          const heading = document.createElement('summary');
          heading.textContent = `▤ ${page.folder}`;
          folder.append(heading);
          tree.before(folder);
          folders.set(page.folder, folder);
        }
        folder.append(row);
      } else tree.before(row);
    });
    this.project.deletedPages.forEach((page) => {
      const row = document.createElement('div');
      row.className = 'deleted-page-row';
      row.innerHTML = `<span>↺ ${escapePageText(page.name)}</span><button data-page-action="restore" data-page-id="${escapePageText(page.id)}">Restore</button>`;
      tree.before(row);
    });
  }

  private renderMenus(panel: Element, before: Element): void {
    const menus = ensureMenus(this.project);
    let manager = panel.querySelector<HTMLElement>('[data-menu-manager]');
    if (!manager) {
      manager = document.createElement('section');
      manager.dataset.menuManager = 'true';
      manager.className = 'menu-manager';
      before.before(manager);
    }
    const selectedId =
      manager.querySelector<HTMLSelectElement>('[data-menu-select]')?.value ?? menus[0].id;
    const menu = menus.find((item) => item.id === selectedId) ?? menus[0];
    const renderItems = (items: typeof menu.items, depth = 0): string =>
      items
        .map(
          (item) =>
            `<div class="menu-item-row" style="--menu-depth:${depth}" data-menu-item="${escapePageText(item.id)}"><span>${item.type === 'page' ? '▧' : item.type === 'external' ? '↗' : '•'}</span><strong>${escapePageText(item.label)}</strong><small>${escapePageText(item.target ?? this.project.pages.find((page) => page.id === item.pageId)?.slug ?? '')}</small><button type="button" data-menu-action="up" aria-label="Move item up">↑</button><button type="button" data-menu-action="down" aria-label="Move item down">↓</button><button type="button" data-menu-action="edit" aria-label="Edit menu item">Aa</button><button type="button" data-menu-action="delete" aria-label="Delete menu item">×</button></div>${item.children?.length ? `<details class="menu-subtree" open><summary>Dropdown · ${escapePageText(item.label)}</summary>${renderItems(item.children, depth + 1)}</details>` : ''}`,
        )
        .join('');
    manager.innerHTML = `<div class="menu-manager-head"><div><p class="eyebrow">Navigation</p><strong>Menu manager</strong></div><button type="button" class="mini-btn" data-menu-action="new">＋</button></div><div class="menu-manager-controls"><select data-menu-select aria-label="Select menu">${menus.map((item) => `<option value="${escapePageText(item.id)}" ${item.id === menu.id ? 'selected' : ''}>${escapePageText(item.name)}</option>`).join('')}</select><select data-menu-mobile aria-label="Mobile navigation behavior"><option value="drawer" ${menu.mobileMode === 'drawer' ? 'selected' : ''}>Mobile drawer</option><option value="stack" ${menu.mobileMode === 'stack' ? 'selected' : ''}>Stack links</option><option value="scroll" ${menu.mobileMode === 'scroll' ? 'selected' : ''}>Horizontal scroll</option></select><button type="button" class="ghost-btn" data-menu-action="add">Add item</button></div><div class="menu-item-list">${renderItems(menu.items) || '<small class="panel-note">No items yet. Add a page, external link, anchor, or button.</small>'}</div>`;
    manager
      .querySelector('[data-menu-select]')
      ?.addEventListener('change', () => this.renderMenus(panel, before));
    manager.querySelector('[data-menu-mobile]')?.addEventListener('change', (event) => {
      menu.mobileMode = (event.target as HTMLSelectElement).value as 'drawer' | 'stack' | 'scroll';
      this.scheduleSave('Menu behavior changed');
    });
  }

  private async deletePage(pageId: string): Promise<void> {
    if (this.project.pages.length <= 1) {
      this.toast('Keep one page', 'The last page cannot be deleted.');
      return;
    }
    const page = this.project.pages.find((item) => item.id === pageId);
    if (!page || !(await askConfirm(`Move “${page.name}” to deleted pages?`))) return;
    const removed = removePage(this.project, pageId);
    if (!removed) return;
    this.renderPages();
    this.switchPage(this.project.currentPageId);
  }

  private reorderPage(pageId: string, delta: number): void {
    const index = this.project.pages.findIndex((page) => page.id === pageId);
    const next = index + delta;
    if (index < 0 || next < 0 || next >= this.project.pages.length) return;
    [this.project.pages[index], this.project.pages[next]] = [
      this.project.pages[next],
      this.project.pages[index],
    ];
    this.renderPages();
    this.saveNow('Pages reordered');
  }

  private movePageBefore(pageId: string, targetId: string): void {
    const from = this.project.pages.findIndex((page) => page.id === pageId);
    const target = this.project.pages.findIndex((page) => page.id === targetId);
    if (from < 0 || target < 0 || from === target) return;
    const [page] = this.project.pages.splice(from, 1);
    const insertion = this.project.pages.findIndex((item) => item.id === targetId);
    this.project.pages.splice(Math.max(0, insertion), 0, page);
  }

  private handleShortcut(event: KeyboardEvent): void {
    const typing =
      ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName ?? '') ||
      document.activeElement?.getAttribute('contenteditable') === 'true';
    if (!typing && (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      this.openCommandPalette();
      return;
    }
    if (event.key === 'Escape' && !document.querySelector<HTMLElement>('#commandPalette')?.hidden) {
      this.closeCommandPalette();
      return;
    }
    if (!typing && (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();
      this.saveNow('Manual save');
    } else if (!typing && (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      this.adapter.undo();
    } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c' && !typing) {
      if (this.selected) {
        this.clipboard = this.selected.clone();
        this.toast('Copied element', 'Paste it anywhere in the layer tree.');
      }
    } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'x' && !typing) {
      if (this.selected && !this.isProtected(this.selected)) {
        this.clipboard = this.selected.clone();
        this.deleteSelected();
      }
    } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'v' && !typing) {
      event.preventDefault();
      this.pasteClipboard();
    } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'd' && !typing) {
      event.preventDefault();
      this.duplicateSelected();
    } else if (event.key === 'Delete' && !typing) {
      event.preventDefault();
      this.deleteSelected();
    }
  }

  private pasteClipboard(): void {
    if (!this.clipboard) return;
    const parent =
      this.selected && this.isContainer(this.selected)
        ? this.selected
        : (this.selected?.parent() ?? undefined);
    const copy = firstComponent(this.adapter.addComponent(this.clipboard.clone(), parent));
    if (copy) {
      this.stripDuplicateIds(copy);
      this.adapter.selectComponent(copy);
      this.scheduleSave();
    }
  }

  private stripDuplicateIds(component: Component): void {
    const attrs = component.getAttributes() as Record<string, string>;
    if (attrs.id) component.addAttributes({ id: `${attrs.id}-${crypto.randomUUID().slice(0, 8)}` });
    component.components().models.forEach((child) => this.stripDuplicateIds(child));
  }

  markDirty(label = 'Autosave'): void {
    this.scheduleSave(label);
  }

  private scheduleSave(label = 'Autosave'): void {
    const state = document.querySelector('#saveState');
    state?.replaceChildren(document.createTextNode('Saving… · WebKiln'));
    window.clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(() => this.saveNow(label), 450);
  }

  private saveNow(label: string): void {
    const page = this.project.pages.find((item) => item.id === this.project.currentPageId);
    if (page) {
      page.projectData = this.adapter.exportProjectData();
      page.updatedAt = new Date().toISOString();
    }
    if (label !== 'Autosave') {
      this.project.revisions.unshift({
        id: crypto.randomUUID(),
        label,
        createdAt: new Date().toISOString(),
        pageId: this.project.currentPageId,
        projectData: page?.projectData ?? null,
      });
      this.project.revisions = this.project.revisions.slice(0, 20);
    }
    this.storage.save(this.project);
    document.dispatchEvent(new CustomEvent('webkiln:project-saved'));
    document
      .querySelector('#saveState')
      ?.replaceChildren(document.createTextNode('✓ Saved · WebKiln'));
    this.renderLayers();
  }

  private bindCustomCode(): void {
    const modal = document.querySelector<HTMLElement>('#codeModal');
    const input = document.querySelector<HTMLTextAreaElement>('#codeInput');
    const keys = ['html', 'css', 'javascript'] as const;
    let activeKey: (typeof keys)[number] = 'html';
    const syncInput = () => {
      if (input) input.value = this.project.customCode[activeKey] ?? '';
      document.querySelectorAll<HTMLButtonElement>('.code-tabs button').forEach((button, index) => {
        button.classList.toggle('active', keys[index] === activeKey);
      });
    };
    const storeInput = () => {
      if (input) this.project.customCode[activeKey] = input.value;
    };
    document.querySelector('#codeEditorBtn')?.addEventListener('click', async () => {
      activeKey = 'html';
      syncInput();
      if (modal) modal.hidden = false;
      input?.focus();
    });
    document.querySelector('#closeCode')?.addEventListener('click', async () => {
      if (modal) modal.hidden = true;
    });
    document.querySelector('#cancelCode')?.addEventListener('click', async () => {
      if (modal) modal.hidden = true;
    });
    document.querySelectorAll<HTMLButtonElement>('.code-tabs button').forEach((button, index) => {
      button.addEventListener('click', async () => {
        storeInput();
        activeKey = keys[index] ?? 'html';
        syncInput();
        input?.focus();
      });
    });
    document.querySelector('#saveCode')?.addEventListener('click', async () => {
      storeInput();
      this.project.customCode.isolated = true;
      this.storage.save(this.project);
      if (modal) modal.hidden = true;
      this.toast('Sandbox code saved', 'Custom code remains isolated from the editor shell.');
    });
  }

  private bindSiteSetup(): void {
    const modal = document.querySelector<HTMLElement>('#setupModal');
    const open = () => {
      const site = this.project.site;
      const setValue = (id: string, value: string) => {
        const input = document.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`);
        if (input) input.value = value;
      };
      setValue('siteName', site.title);
      setValue('siteDescription', site.description);
      setValue('businessType', site.businessType ?? 'Hosting & infrastructure');
      setValue('siteLanguage', site.language || 'en');
      setValue('siteTimezone', site.timezone || 'UTC');
      const seo = document.querySelector<HTMLInputElement>('#seoBasics');
      const analytics = document.querySelector<HTMLInputElement>('#analyticsPlaceholder');
      if (seo) seo.checked = site.seoEnabled !== false;
      if (analytics) analytics.checked = site.analyticsPlaceholder !== false;
      if (modal) modal.hidden = false;
    };
    document.querySelector('#openSiteSetup')?.addEventListener('click', open);
    document.querySelector('#closeSetup')?.addEventListener('click', async () => {
      if (modal) modal.hidden = true;
    });
    document.querySelector('#cancelSetup')?.addEventListener('click', async () => {
      if (modal) modal.hidden = true;
    });
    document.querySelector('#saveSetup')?.addEventListener('click', async () => {
      const value = (id: string) =>
        document.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`)?.value.trim() ?? '';
      const title = value('siteName');
      if (!title) {
        document.querySelector<HTMLInputElement>('#siteName')?.focus();
        return;
      }
      this.project.site.title = title;
      this.project.site.description = value('siteDescription');
      this.project.site.businessType = value('businessType');
      this.project.site.language = value('siteLanguage') || 'en';
      this.project.site.timezone = value('siteTimezone') || 'UTC';
      this.project.site.seoEnabled =
        document.querySelector<HTMLInputElement>('#seoBasics')?.checked ?? true;
      this.project.site.analyticsPlaceholder =
        document.querySelector<HTMLInputElement>('#analyticsPlaceholder')?.checked ?? true;
      const home = this.project.pages.find((page) => page.id === this.project.homepagePageId);
      if (home && this.project.site.seoEnabled) {
        home.seo = {
          ...(home.seo ?? { title: title, description: '' }),
          title,
          description: this.project.site.description,
        };
      }
      document.querySelector('.crumb span')?.replaceChildren(document.createTextNode(title));
      if (modal) modal.hidden = true;
      this.saveNow('Site settings updated');
      this.toast('Site settings saved', 'SEO metadata and project identity were updated.');
    });
  }

  private bindRecovery(): void {
    const backup = this.storage.getLegacyBackup();
    if (!backup) return;
    const card = document.createElement('div');
    card.className = 'recovery-card';
    card.innerHTML = `<strong>Legacy backup available</strong><small>${backup.key} · ${backup.value.length.toLocaleString()} characters</small><div><button type="button" data-recover="restore">Restore</button><button type="button" data-recover="export">Export</button></div>`;
    card.addEventListener('click', async (event) => {
      const action = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-recover]')
        ?.dataset.recover;
      if (
        action === 'restore' &&
        (await askConfirm(
          'Restore the saved legacy backup? Current pages remain available in local storage.',
        ))
      ) {
        const restored = this.storage.restoreLatestLegacy();
        if (restored) {
          window.location.reload();
        }
      }
      if (action === 'export') {
        const blob = new Blob([backup.value], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `webkiln-${backup.key}-backup.json`;
        link.click();
        URL.revokeObjectURL(url);
      }
    });
    document.querySelector('#sitePanel')?.append(card);
  }

  private toast(title: string, detail: string): void {
    const toast = document.querySelector('#toast');
    if (!toast) return;
    toast.querySelector('strong')?.replaceChildren(document.createTextNode(title));
    toast.querySelector('small')?.replaceChildren(document.createTextNode(detail));
    toast.classList.add('show');
    window.setTimeout(() => toast.classList.remove('show'), 3000);
  }

  private toHex(value: string): string {
    if (value.startsWith('#')) return value;
    const match = value.match(/\d+/g);
    if (!match || match.length < 3) return '#0b0d0f';
    return `#${match
      .slice(0, 3)
      .map((part) => Number(part).toString(16).padStart(2, '0'))
      .join('')}`;
  }
}
